import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';

// Sub-components
import { CallCardStatusBanner } from '@/components/callcard/CallCardStatusBanner';
import { MdtBottomBar } from '@/components/callcard/MdtBottomBar';
import { DisabledFieldToast } from '@/components/callcard/DisabledFieldToast';

// Tab components
import { CallCardDetailsTab } from '@/components/callcard/tabs/CallCardDetailsTab';
import { CallCardPeopleVehiclesTab } from '@/components/callcard/tabs/CallCardPeopleVehiclesTab';
import { CallCardAttachmentsTab } from '@/components/callcard/tabs/CallCardAttachmentsTab';
import { CallCardCitationsTab } from '@/components/callcard/tabs/CallCardCitationsTab';
import { CallCardAuditLogTab } from '@/components/callcard/tabs/CallCardAuditLogTab';
import { CallCardHistoryTab } from '@/components/callcard/tabs/CallCardHistoryTab';

import { DEFAULT_CALL_CARD_PRIORITY } from '@/constants/priorities';

// Modals
import { ConfirmModal } from '@/components/ConfirmModal';
import { CloseCallModal } from '@/components/modals/CloseCallModal';
import { TimeUpdateModal } from '@/components/modals/TimeUpdateModal';
import { ViewSopModal } from '@/components/modals/ViewSopModal';
import { PageModal } from '@/components/modals/PageModal';
import { SearchModal } from '@/components/modals/SearchModal';
import { AddEditCustomLocationModal } from '@/components/modals/AddEditCustomLocationModal';
import { NPSDPersonScreen } from '@/components/npsd/NPSDPersonScreen';
import { NPSDVehicleScreen } from '@/components/npsd/NPSDVehicleScreen';
import { NPSDClientScreenWrapper } from '@/components/npsd/NPSDClientScreen';
import { MapScreen } from '@/components/screens/MapScreen';
import { navigateToMapByCallCardId, navigateToNewCallCard, refreshPage, navigateRaw } from '@/services/navigation';

// ============================================================================
// External Declarations (global scope from legacy JS)
// ============================================================================

import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment-timezone';
import { hasValue, hasStringValue, currentUserHasPermission, getOfficerLabel } from '@/utils/legacy-utils';
import { getRelativeUrlForMapByCallCardId } from '@/utils/dom';
import { addCompanyNameField, listHasOtherCompanyItems } from '@/utils/call-card-helpers';
import { AccessAuthorities } from '@/models';
import { addCallCommentHelper, updateCallCommentEntry } from '@/models/legacy/index';
import { mdtHasPermissionsToEditCallCard } from '@/services/mdt-service';
import { Codes } from '@/constants/codes';
import { Fuse } from '@/utils/vendor-compat';

import { ModuleType } from '@/constants/codes';
import { CallCardStatus, CCAction } from '@/services/api-types';
import { RelType, OrnaNavigationType } from '@/models/other-entities';

import { hasNumericValue } from '@/utils/validation';
import { getCurrentUserId, getCurrentCompanyId, getAvailableSubmodules, getAuthObject } from '@/utils/auth';
import { isInArray } from '@/utils/arrays';
import { getOfficerPluralLabel } from '@/utils/auth';
import { deepCopyProperties, DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT, isUWPApp, isIOSApp, isAndroidApp, isWindowsAppSdkApp } from '@/utils/window-globals';
// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const convertOfficersForGrid = _wg('convertOfficersForGrid');
const convertPeopleForGrid = _wg('convertPeopleForGrid');
const convertVehiclesForGrid = _wg('convertVehiclesForGrid');
const processCallCardAvailableOfficers = _wg('processCallCardAvailableOfficers');
const setCallCardRealtimeEnabled = _wg('setCallCardRealtimeEnabled');
const setCallCardScreenLoaded = _wg('setCallCardScreenLoaded');
const removeMDTActiveCallCard = _wg('removeMDTActiveCallCard');
const addMDTActiveCallCard = _wg('addMDTActiveCallCard');
const setShowingRecentOfficerAssignedDialogOnPageLoaded = _wg('setShowingRecentOfficerAssignedDialogOnPageLoaded');
const showNewCaseReport = _wg('showNewCaseReport');
const addSelfToCallCardMDT = _wg('addSelfToCallCardMDT');
const updateMDTEditModeAllowed = _wg('updateMDTEditModeAllowed');
const dialogCheckIfUserWantsToEditOrReopenCallCardWithClientNotifications = _wg('dialogCheckIfUserWantsToEditOrReopenCallCardWithClientNotifications');
const loadNocSopImages = _wg('loadNocSopImages');
const getPatrolZoneNames = _wg('getPatrolZoneNames');
const getCustomLocationListPromise = _wg('getCustomLocationListPromise');
const callCardLinkToPerson = _wg('callCardLinkToPerson');
const callCardLinkToVehicle = _wg('callCardLinkToVehicle');
const callCardAddNpsdPerson = _wg('callCardAddNpsdPerson');
const callCardAddNpsdVehicle = _wg('callCardAddNpsdVehicle');
const NPSDItemToCallCard = _wg('NPSDItemToCallCard');
const setAuthObject = _wg('setAuthObject');
const getInsecureUUID = _wg('getInsecureUUID');
const getObjectInArrayByProperty = _wg('getObjectInArrayByProperty');
const removeObjectFromArrayByProperty = _wg('removeObjectFromArrayByProperty');
const arraysEqual = _wg('arraysEqual');
const getNullableDateClone = _wg('getNullableDateClone');
const isoDatetimeToFormattedDatetime = _wg('isoDatetimeToFormattedDatetime');
const goToInProgressScreen = _wg('goToInProgressScreen');
const mergeCurrentNatureOfCallIntoNatureOfCallList = _wg('mergeCurrentNatureOfCallIntoNatureOfCallList');
const mergeCurrentCallDispositionIntoCallDispositionList = _wg('mergeCurrentCallDispositionIntoCallDispositionList');
const getRelativeUrlForMapLocation = _wg('getRelativeUrlForMapLocation');
const getOfficerListFromCallCard = _wg('getOfficerListFromCallCard');
// addCallCommentHelper and updateCallCommentEntry imported from @/models/legacy/index
const sortAndRemoveDuplicates = _wg('sortAndRemoveDuplicates');
import { b64toBlob } from '@/utils/blob';
import { saveAs } from 'file-saver';
const notifyApp = _wg('notifyApp');
const startTurnByTurnNavigationV2 = _wg('startTurnByTurnNavigationV2');
const isInAppNavigationAvailable = _wg('isInAppNavigationAvailable');
const getOrnaNavigationTypeWithDefault = _wg('getOrnaNavigationTypeWithDefault');
const setLocalOrnaNavigationType = _wg('setLocalOrnaNavigationType');
import { validateAddressWithSuggestions } from '@/utils/custom-location';
const fillInAddressFromSuggestions = _wg('fillInAddressFromSuggestions');
const getCCVehicleTypeFromVehicleType = _wg('getCCVehicleTypeFromVehicleType');

// Value lookups: access lazily at usage sites via (window as any).X

// Constructors: access lazily since they're defined by MyBuiltScripts.js
const _cls = (name: string): any => {
  const handler = {
    construct(_target: any, args: any[]) { return new (window as any)[name](...args); },
    apply(_target: any, _thisArg: any, args: any[]) { return (window as any)[name](...args); },
  };
  return new Proxy(function(){} as any, handler);
};
const CallComment = _cls('CallComment');
const Officer = _cls('Officer');
const CCPerson = _cls('CCPerson');
const CCVehicle = _cls('CCVehicle');
const Person = _cls('Person');
const Vehicle = _cls('Vehicle');
const Attachment = _cls('Attachment');
const AttachmentInfo = _cls('AttachmentInfo');

// ============================================================================
// Types
// ============================================================================

interface CallCardScreenProps {
  CC: any;
  CCCntr: any;
  moduleType: any;
}

type TabId = 'CallDetails' | 'PeopleVehicles' | 'Attachments' | 'Citations' | 'AuditLog' | 'History';

interface TimeUpdateModalState {
  open: boolean;
  type: '' | 'enroute' | 'onScene' | 'cleared';
  title: string;
  message: string;
  initialValue: Date | null;
}

interface ClientMatchModalState {
  open: boolean;
  clientName: string;
  clientData: any;
  pendingAction: Function | null;
}

interface ConnectionsSearchState {
  open: boolean;
  type: string;
  initialValues: Record<string, any>;
  callback: Function | null;
}

// ============================================================================
// Component
// ============================================================================

export function CallCardScreen({ CC, CCCntr, moduleType }: CallCardScreenProps) {
  const { theme } = useTheme();

  // ── Refs for imperative access ────────────────────────────────────────
  const officersRef = useRef<any>(null);
  const peopleRef = useRef<any>(null);
  const vehiclesRef = useRef<any>(null);

  // ── Core state ────────────────────────────────────────────────────────

  const [officers, setOfficers] = useState<any[]>([]);
  const [availablePeople, setAvailablePeople] = useState<any[]>([]);
  const [people, setPeople] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>(CC.attachmentList || []);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editModeAllowed, setEditModeAllowed] = useState(true);
  const [callCardNotClosed, setCallCardNotClosed] = useState(CC.ccStatus !== CallCardStatus.CASE_CLOSED);
  const [isEditingClosedCallCard, setIsEditingClosedCallCard] = useState(false);
  const [isOfficerOnCallCard, setIsOfficerOnCallCard] = useState(false);
  const [callCardCreatedBeforeAuditLogging, setCallCardCreatedBeforeAuditLogging] = useState(
    hasValue(CC.createdAt) ? CC.createdAt < new Date('2022-06-14 19:40:00 Z') : false
  );

  const [saveOrUpdateButtonEnabled, setSaveOrUpdateButtonEnabled] = useState(true);
  const [pageReady, setPageReady] = useState(false);
  const [clockTime, setClockTime] = useState(new Date().toLocaleTimeString());
  const [realtimeVersion, setRealtimeVersion] = useState(0);
  const [priorityAutoChangeVersion, setPriorityAutoChangeVersion] = useState(0);
  // ── Permission flags ──────────────────────────────────────────────────

  const isMDT = useMemo(() => moduleType === ModuleType.mdt, [moduleType]);
  const hasCCAuditLogPermission = useMemo(
    () => isInArray(getAvailableSubmodules(), AccessAuthorities.ROLE_CALL_CARD_AUDIT_LOG), []
  );
  const hasCCDownloadPermission = useMemo(
    () => isInArray(getAvailableSubmodules(), AccessAuthorities.ROLE_CALL_CARD_PDF), []
  );
  const hasCallCommentUpdatePermission = useMemo(
    () => isInArray(getAvailableSubmodules(), AccessAuthorities.ROLE_CALL_COMMENT_UPDATE), []
  );
  const hasCitationViewPermission = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_CITATION_VIEW), []
  );
  const hasCloseCallCardPermissions = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_CALL_CARD_CLOSE), []
  );
  const hasUpdateClosedCallCardPermissions = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_UPDATE_CLOSED_CALL_CARDS), []
  );
  const hasReopenClosedCallCardPermissions = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_CALL_CARD_REOPEN), []
  );

  // ── Dropdown data ─────────────────────────────────────────────────────

  const [natureOfCallOptions, setNatureOfCallOptions] = useState<any[]>([]);
  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const [siteOptions, setSiteOptions] = useState<any[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [dispositionOptions, setDispositionOptions] = useState<any[]>([]);
  const [initialClientId, setInitialClientId] = useState<number | null>(null);
  const [initialClientAllSites, setInitialClientAllSites] = useState<any[]>([]);

  // ── Links & computed HTML ─────────────────────────────────────────────

  const [clientLink, setClientLink] = useState('');
  const [natureOfCallSopLink, setNatureOfCallSopLink] = useState('');
  const [natureOfCallTempSopData, setNatureOfCallTempSopData] = useState<any>(null);
  const [patrolZoneNames, setPatrolZoneNames] = useState<string[]>([]);
  const [callAlert, setCallAlert] = useState<any>(null);

  // ── Tab system ────────────────────────────────────────────────────────

  const [activeTab, setActiveTab] = useState<TabId>('CallDetails');
  const [visibleTabIds, setVisibleTabIds] = useState<string[]>([]);
  const isCalculatingOverflowRef = useRef(false);
  const tabWidthCacheRef = useRef<Map<string, number>>(new Map());
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // ── Modal state ───────────────────────────────────────────────────────

  const [showDeleteCallModal, setShowDeleteCallModal] = useState(false);
  const [showCreateCaseReportModal, setShowCreateCaseReportModal] = useState(false);
  const [createCaseReportEvent, setCreateCaseReportEvent] = useState<any>(null);

  const [timeUpdateModal, setTimeUpdateModal] = useState<TimeUpdateModalState>({
    open: false, type: '', title: '', message: '', initialValue: null,
  });

  const [showSopModal, setShowSopModal] = useState(false);
  const [sopModalLoading, setSopModalLoading] = useState(false);
  const [sopModalContent, setSopModalContent] = useState('');

  const [showClientModal, setShowClientModal] = useState(false);
  const [clientModalClientId, setClientModalClientId] = useState('');

  const [showMapModal, setShowMapModal] = useState(false);
  const [mapModalAddress, setMapModalAddress] = useState('');

  const [showPersonModal, setShowPersonModal] = useState(false);
  const [personModalId, setPersonModalId] = useState<number | null>(null);
  const [personModalUuid, setPersonModalUuid] = useState<string | null>(null);

  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [vehicleModalId, setVehicleModalId] = useState<number | null>(null);
  const [vehicleModalUuid, setVehicleModalUuid] = useState<string | null>(null);

  const [connectionsSearch, setConnectionsSearch] = useState<ConnectionsSearchState>({
    open: false, type: '', initialValues: {}, callback: null,
  });

  const [showNavigationSettingsModal, setShowNavigationSettingsModal] = useState(false);
  const [selectedNavigationType, setSelectedNavigationType] = useState<number>(OrnaNavigationType.GOOGLE_MAPS);
  const [savingNavigationSettings, setSavingNavigationSettings] = useState(false);

  const [showCloseCallModal, setShowCloseCallModal] = useState(false);

  const [clientMatchModal, setClientMatchModal] = useState<ClientMatchModalState>({
    open: false, clientName: '', clientData: null, pendingAction: null,
  });

  const [showAddCustomLocationModal, setShowAddCustomLocationModal] = useState(false);
  const [savingCustomLocation, setSavingCustomLocation] = useState(false);
  const [addCustomLocationData, setAddCustomLocationData] = useState<{ location: string; coordinates: string }>({
    location: '', coordinates: '',
  });

  // ── Labels ────────────────────────────────────────────────────────────

  const officerLabel = useMemo(() => getOfficerLabel(), []);
  const officerPluralLabel = useMemo(() => getOfficerPluralLabel(), []);

  // ── Refs for latest state in callbacks ────────────────────────────────

  const officersStateRef = useRef(officers);
  officersStateRef.current = officers;
  const peopleStateRef = useRef(people);
  peopleStateRef.current = people;
  const vehiclesStateRef = useRef(vehicles);
  vehiclesStateRef.current = vehicles;
  const callCardNotClosedRef = useRef(callCardNotClosed);
  callCardNotClosedRef.current = callCardNotClosed;
  const isEditModeRef = useRef(isEditMode);
  isEditModeRef.current = isEditMode;
  const isEditingClosedCallCardRef = useRef(isEditingClosedCallCard);
  isEditingClosedCallCardRef.current = isEditingClosedCallCard;
  const clientOptionsRef = useRef(clientOptions);
  clientOptionsRef.current = clientOptions;
  const siteOptionsRef = useRef(siteOptions);
  siteOptionsRef.current = siteOptions;
  const natureOfCallOptionsRef = useRef(natureOfCallOptions);
  natureOfCallOptionsRef.current = natureOfCallOptions;
  const connectionsSearchRef = useRef(connectionsSearch);
  connectionsSearchRef.current = connectionsSearch;
  const showPersonModalRef = useRef(showPersonModal);
  showPersonModalRef.current = showPersonModal;
  const showVehicleModalRef = useRef(showVehicleModal);
  showVehicleModalRef.current = showVehicleModal;
  const showClientModalRef = useRef(showClientModal);
  showClientModalRef.current = showClientModal;
  const clockTimeRef = useRef(clockTime);
  clockTimeRef.current = clockTime;

  // ── Computed properties ───────────────────────────────────────────────

  const hasDisposition = useMemo(() => {
    return hasValue(CC) && hasValue(CC.callDisposition);
  }, [CC?.callDisposition]);

  // Cross-company dispatchers see items from the companies they dispatch for;
  // group those pickers by company name so it's clear which company owns each item.
  const natureOfCallGroupBy = useMemo(() => {
    return listHasOtherCompanyItems(natureOfCallOptions) ? 'companyName' : null;
  }, [natureOfCallOptions]);

  const dispositionGroupBy = useMemo(() => {
    return listHasOtherCompanyItems(dispositionOptions) ? 'companyName' : null;
  }, [dispositionOptions]);

  const isFormDisabled = useMemo((): boolean => {
    if (isMDT) {
      return !isEditMode;
    } else {
      return !callCardNotClosed && !isEditingClosedCallCard;
    }
  }, [isMDT, isEditMode, callCardNotClosed, isEditingClosedCallCard]);

  const mdtOfficerTimeButtonsEnabled = useMemo(
    () => callCardNotClosed && isOfficerOnCallCard,
    [callCardNotClosed, isOfficerOnCallCard]
  );

  // Same edit gate as the form: CAD edits any open call; MDT needs mdtHasPermissionsToEditCallCard.
  const canEditCallLocation = useMemo(
    () => callCardNotClosed && (!isMDT || editModeAllowed),
    [callCardNotClosed, isMDT, editModeAllowed]
  );

  const canEnterMDTEditMode = useMemo(
    () => editModeAllowed && !isEditMode && callCardNotClosed,
    [editModeAllowed, isEditMode, callCardNotClosed]
  );

  // Refs for computed values accessed by external code (Shortcuts.ts etc.)
  const mdtOfficerTimeButtonsEnabledRef = useRef(mdtOfficerTimeButtonsEnabled);
  mdtOfficerTimeButtonsEnabledRef.current = mdtOfficerTimeButtonsEnabled;
  const canEnterMDTEditModeRef = useRef(canEnterMDTEditMode);
  canEnterMDTEditModeRef.current = canEnterMDTEditMode;

  const isSelfOnCall = useCallback((): boolean => {
    if (!hasValue(CC)) return false;
    const currentUserId = getCurrentUserId();
    if (hasValue(CC.primaryOfficer) && CC.primaryOfficer === currentUserId) {
      if (!hasValue(CC.primaryCleared)) return true;
    }
    if (CC.backupUnits !== null) {
      for (const backupUnit of CC.backupUnits) {
        if (backupUnit.officer !== currentUserId) continue;
        if (!hasValue(backupUnit.cleared)) return true;
      }
    }
    return false;
  }, [CC]);

  const canSelfAssignToCall = useMemo(
    () => isMDT && callCardNotClosed && !isSelfOnCall(),
    [isMDT, callCardNotClosed, isSelfOnCall]
  );

  const currentOfficer = useMemo(() => {
    const currentUserId = getCurrentUserId();
    return officers.find((o: any) => o.officerId === currentUserId) || null;
  }, [officers]);

  const enrouteDisabled = useMemo(() => {
    const hasPermission = currentUserHasPermission(AccessAuthorities.ROLE_MDT_UPDATE_RESPONSE_TIMES);
    return !hasPermission && (currentOfficer ? hasValue(currentOfficer.enroute) : false);
  }, [currentOfficer]);

  const onSceneDisabled = useMemo(() => {
    const hasPermission = currentUserHasPermission(AccessAuthorities.ROLE_MDT_UPDATE_RESPONSE_TIMES);
    return !hasPermission && (currentOfficer ? hasValue(currentOfficer.onScene) : false);
  }, [currentOfficer]);

  const clearedDisabled = useMemo(() => {
    const hasPermission = currentUserHasPermission(AccessAuthorities.ROLE_MDT_UPDATE_RESPONSE_TIMES);
    return !hasPermission && (currentOfficer ? hasValue(currentOfficer.cleared) : false);
  }, [currentOfficer]);

  const classEnrouteBtn = useMemo(() => {
    return { MDTResponseEnroute: currentOfficer ? hasValue(currentOfficer.enroute) : false };
  }, [currentOfficer]);

  const classOnSceneBtn = useMemo(() => {
    return { MDTResponseOnScene: currentOfficer ? hasValue(currentOfficer.onScene) : false };
  }, [currentOfficer]);

  const classClearedBtn = useMemo(() => {
    return { MDTResponseCleared: currentOfficer ? hasValue(currentOfficer.cleared) : false };
  }, [currentOfficer]);

  const isShowingClientAddressButton = useMemo(() => {
    return CC.client && CC.client.id && callCardNotClosed && (!isMDT || isEditMode);
  }, [CC?.client?.id, callCardNotClosed, isMDT, isEditMode]);

  const canSendCallComments = useMemo((): boolean => {
    if (!hasValue(CC) || !hasValue(CCCntr)) return false;
    if (isFormDisabled) return false;
    if (!callCardNotClosed) {
      if (!isEditingClosedCallCard) return false;
    }
    if (isMDT) {
      const availableSubmodules = getAvailableSubmodules();
      if (isInArray(availableSubmodules, 'ROLE_MDT_CALL_COMMENT_SAVE')) return true;
      if (isInArray(availableSubmodules, 'ROLE_MDT_CALL_COMMENT_SAVE_IF_ON_CALL') && isSelfOnCall()) return true;
      return false;
    }
    return true;
  }, [CC, CCCntr, isFormDisabled, callCardNotClosed, isEditingClosedCallCard, isMDT, isSelfOnCall]);

  // ── Tab system computed ───────────────────────────────────────────────

  const availableTabIds = useMemo((): TabId[] => {
    const tabs: TabId[] = ['CallDetails', 'PeopleVehicles', 'Attachments'];
    if (hasCitationViewPermission) tabs.push('Citations');
    if (CC.id != null && hasCCAuditLogPermission) tabs.push('AuditLog');
    if (CC.id != null) tabs.push('History');
    return tabs;
  }, [CC?.id, hasCitationViewPermission, hasCCAuditLogPermission]);

  const overflowTabIds = useMemo(
    () => availableTabIds.filter(id => !visibleTabIds.includes(id)),
    [availableTabIds, visibleTabIds]
  );

  const hasOverflowTabs = useMemo(() => overflowTabIds.length > 0, [overflowTabIds]);

  // ── Call location map link (computed HTML) ────────────────────────────

  const callLocationMapLink = useMemo((): string => {
    const address = CC.callLocFormattedAddress;
    if (!hasStringValue(address)) return '';
    // A saved call opens its map by id so the stored coordinate answers without a geocode;
    // only an unsaved call (no id yet) falls back to the by-address route.
    const relativeUrl = hasValue(CC.id)
      ? getRelativeUrlForMapByCallCardId(CC.id)
      : getRelativeUrlForMapLocation(address);
    let linksHtml = ` <a href="javascript:void(0)" onclick="window.OrnaReact.navigate.raw('${relativeUrl}')" title='View on Map' style='font-size:13px;line-height:1.42857143'>Map</a>`;
    const isIOSorAndroidApp = isIOSApp || isAndroidApp;
    if (isIOSorAndroidApp) {
      linksHtml += `<a href="javascript:void(0)" onclick="App.callCardController.openMapModal()" title='View on Map in Window' style='margin-left:5px;font-size:13px;line-height:1.42857143'>[Window]</a>`;
    } else {
      linksHtml += `<a href="${relativeUrl}" target="_blank" title='View on Map in New Window' style='margin-left:5px;font-size:13px;line-height:1.42857143'>[+Window]</a>`;
    }
    // If callLocFormattedAddress is coordinates
    const coordinatesArr = address.split(',');
    if (coordinatesArr.length === 2) {
      const latitude = parseFloat(coordinatesArr[0]);
      const longitude = parseFloat(coordinatesArr[1]);
      let submodules = getAvailableSubmodules() || [];
      if (hasNumericValue(latitude) && hasNumericValue(longitude) && isInArray(submodules, AccessAuthorities.ROLE_CUSTOM_LOCATION_SAVE)) {
        const coordsString = latitude + ',' + longitude;
        linksHtml += `<a href="javascript:void(0)" id='lnkCallCardAddCustomLocation' data-coords=${coordsString} title='Add Custom Location' style='margin-left:5px;font-size:13px;line-height:1.42857143;float:right'>+Custom Location</a>`;
      }
    }
    return linksHtml;
  }, [CC?.callLocFormattedAddress, CC?.id]);

  // ── Tab label map ─────────────────────────────────────────────────────

  const peopleVehicleCount = useMemo(() => (people?.length || 0) + (vehicles?.length || 0), [people, vehicles]);
  const attachmentCount = attachments.length;
  const citationCount = useMemo(() => CC.citationCount || 0, [CC.citationCount]);

  const tabLabels: Record<TabId, React.ReactNode> = useMemo(() => ({
    CallDetails: 'Call Details',
    PeopleVehicles: <>People &amp; Vehicles <span className="badge">{peopleVehicleCount}</span></>,
    Attachments: <>Attachments <span className="badge">{attachmentCount}</span></>,
    Citations: <>Citations <span className="badge">{citationCount}</span></>,
    AuditLog: 'Audit Log',
    History: 'History',
  }), [peopleVehicleCount, attachmentCount, citationCount]);

  // ── Methods: Link generation ──────────────────────────────────────────

  const fnUpdateCallCardClientLink = useCallback((selectedItemId: any, selectedItemName: any, selectedItemCompanyId: any) => {
    if (hasStringValue(selectedItemId) && hasStringValue(selectedItemCompanyId) && (getCurrentCompanyId().toString() === selectedItemCompanyId.toString())) {
      setClientLink(` <a href="javascript:void(0)" onclick="App.callCardController.openClientModal('${selectedItemId}')" title='${selectedItemName}' style='font-size:13px;line-height:1.42857143'>View/Edit Client</a>`);
    } else {
      setClientLink('');
    }
  }, []);

  const fnUpdateCallCardNatureOfCallSopLink = useCallback((natureOfCallData: any) => {
    if (hasValue(natureOfCallData) && natureOfCallData.some((elt: any) => elt.hasSop)) {
      setNatureOfCallTempSopData(natureOfCallData);
      let linksHtml = ` <a href="javascript:void(0)" onclick="App.callCardController.openSopModal()" title='Standard Operating Procedure(s)' style='font-size:13px;line-height:1.42857143'>SOP</a>`;
      const isIOSorAndroidApp = isIOSApp || isAndroidApp;
      if (!isIOSorAndroidApp) {
        let relativeUrl: string | null = null;
        const mt = App.getModuleTypeFromURL();
        const natureOfCallCodesWithSop = natureOfCallData.filter((e: any) => e.hasSop).map((e: any) => e.code);
        if (mt === ModuleType.cad) {
          relativeUrl = `/?sectionName=CAD&tab=InProgress&function=CAD_CallCardSop&multiModule=true&params=${natureOfCallCodesWithSop.join(',')}`;
        } else if (mt === ModuleType.mdt) {
          relativeUrl = `/?sectionName=MDT&tab=InProgress&function=MDT_CallCardSop&multiModule=true&params=${natureOfCallCodesWithSop.join(',')}`;
        }
        if (relativeUrl) {
          linksHtml += `<a href="${relativeUrl}" target="_blank" title='View SOP in New Window' style='margin-left:5px;font-size:13px;line-height:1.42857143'>[+Window]</a>`;
        }
      }
      setNatureOfCallSopLink(linksHtml);
    } else {
      setNatureOfCallTempSopData(null);
      setNatureOfCallSopLink('');
    }
  }, []);

  // ── Methods: Tab system ───────────────────────────────────────────────

  const calculateVisibleTabs = useCallback(() => {
    if (isCalculatingOverflowRef.current) return;
    isCalculatingOverflowRef.current = true;

    const container = document.querySelector('.cc-tabs-container') as HTMLElement;
    const tabsList = document.querySelector('.cc-tabs-list') as HTMLElement;
    if (!container || !tabsList) {
      isCalculatingOverflowRef.current = false;
      return;
    }

    const tabs = Array.from(tabsList.querySelectorAll('.cc-tab')) as HTMLElement[];
    const gap = 4;
    const buffer = 5;
    const moreBtnWidth = 80; // fixed estimate for "More" button

    // Measure visible tabs and cache their widths; for hidden tabs use cached width
    for (const tab of tabs) {
      const tabId = tab.getAttribute('data-tab-id');
      if (!tabId) continue;
      const w = tab.offsetWidth;
      if (w > 0) {
        tabWidthCacheRef.current.set(tabId, w);
      }
    }

    // Calculate total width of all tabs using cached widths
    const containerWidth = container.clientWidth;
    let totalWidth = 0;
    let idx = 0;
    for (const tabId of availableTabIds) {
      const w = tabWidthCacheRef.current.get(tabId) || 100; // fallback estimate
      totalWidth += w + (idx > 0 ? gap : 0);
      idx++;
    }

    if (totalWidth <= containerWidth + buffer) {
      // All tabs fit
      setVisibleTabIds([...availableTabIds]);
      isCalculatingOverflowRef.current = false;
      return;
    }

    // Not all fit — calculate which ones do, reserving space for "More" button
    const availableWidth = containerWidth - moreBtnWidth;
    totalWidth = 0;
    const newVisibleIds: string[] = [];

    for (const tabId of availableTabIds) {
      const w = tabWidthCacheRef.current.get(tabId) || 100;
      const tabWidth = w + (newVisibleIds.length > 0 ? gap : 0);
      if (totalWidth + tabWidth <= availableWidth) {
        totalWidth += tabWidth;
        newVisibleIds.push(tabId);
      } else {
        break;
      }
    }

    if (newVisibleIds.length < 2) {
      newVisibleIds.length = 0;
      newVisibleIds.push('CallDetails', 'PeopleVehicles');
    }

    setVisibleTabIds(newVisibleIds);
    isCalculatingOverflowRef.current = false;
  }, [availableTabIds]);

  const selectTab = useCallback((tabName: TabId) => {
    setActiveTab(tabName);
  }, []);

  const toggleMoreMenu = useCallback(() => {
    setShowMoreMenu(prev => !prev);
  }, []);

  // ── Methods: Disabled field feedback ──────────────────────────────────

  const showDisabledFieldFeedback = useCallback(() => {
    let message = '';
    if (!callCardNotClosedRef.current && !isEditingClosedCallCardRef.current) {
      if (isMDT) {
        message = 'This call is closed. Use Dispatch module to edit or reopen.';
      } else if (hasUpdateClosedCallCardPermissions && hasReopenClosedCallCardPermissions) {
        message = 'This call is closed. You can edit or reopen it using the buttons above.';
      } else if (hasUpdateClosedCallCardPermissions) {
        message = 'This call is closed. Click "Edit Closed Call" to make changes.';
      } else if (hasReopenClosedCallCardPermissions) {
        message = 'This call is closed. Click "Reopen Call" to reopen it.';
      } else {
        message = 'This call is closed and cannot be modified.';
      }
    } else if (isMDT && !isEditModeRef.current) {
      message = 'Tap the edit button in the toolbar to make changes.';
    } else if (isMDT && !isSelfOnCall()) {
      message = 'You must be assigned to this call. Tap "Self Assign" to join.';
    } else {
      message = 'You do not have permission to modify this field.';
    }
    if (message) App.showPopupNotification(message, 'warning');
  }, [isMDT, hasUpdateClosedCallCardPermissions, hasReopenClosedCallCardPermissions, isSelfOnCall]);

  // ── Methods: Officer changes ──────────────────────────────────────────

  const handleOfficersChange = useCallback((newOfficers: any[]) => {
    if (!newOfficers) return;
    setOfficers(newOfficers);

    // Sync to CallCard model
    CC.backupUnits = [];
    CC.queuedUnits = [];
    let primaryOfficer: any = null;

    for (const vueOfficer of newOfficers) {
      const officer = new Officer(
        vueOfficer.id, vueOfficer.officerId, vueOfficer.name,
        vueOfficer.dispatched, null, vueOfficer.enroute, null,
        vueOfficer.onScene, null, vueOfficer.cleared, null,
        vueOfficer.isPrimary,
        vueOfficer.startingMileage || null, vueOfficer.endingMileage || null,
        vueOfficer.eventId, vueOfficer.callCardId, vueOfficer.isQueued
      );
      if (vueOfficer.isQueued || vueOfficer.isBusy) {
        CC.queuedUnits.push(officer);
      } else if (vueOfficer.isPrimary) {
        primaryOfficer = officer;
      } else {
        CC.backupUnits.push(officer);
      }
    }

    if (primaryOfficer) {
      CC.primaryOfficer = primaryOfficer.officer;
      CC.primaryOfficerName = primaryOfficer.officerName;
      CC.primaryDispatched = primaryOfficer.dispatched;
      CC.primaryEnroute = primaryOfficer.enroute;
      CC.primaryOnScene = primaryOfficer.onScene;
      CC.primaryCleared = primaryOfficer.cleared;
      CC.primaryStartingMileage = primaryOfficer.startingMileage;
      CC.primaryEndingMileage = primaryOfficer.endingMileage;
    } else {
      CC.primaryOfficer = null;
      CC.primaryOfficerName = null;
      CC.primaryDispatched = null;
      CC.primaryEnroute = null;
      CC.primaryOnScene = null;
      CC.primaryCleared = null;
      CC.primaryStartingMileage = null;
      CC.primaryEndingMileage = null;
    }
  }, [CC]);

  // ── Methods: People / Vehicle changes ─────────────────────────────────

  const handlePeopleChange = useCallback((newPeople: any[]) => {
    setPeople(newPeople);
    CC.people = newPeople.map((p: any) => new CCPerson({
      id: p.id, firstName: p.firstName, lastName: p.lastName,
      dob: p.dob, license: p.license, notes: p.notes, npsdPerson: p.npsdPerson,
    }));
  }, [CC]);

  const handleVehiclesChange = useCallback((newVehicles: any[]) => {
    setVehicles(newVehicles);
    CC.vehicles = newVehicles.map((v: any) => new CCVehicle({
      id: v.id, make: v.make, model: v.model, color: v.color, year: v.year,
      plateNumber: v.plateNumber, plateState: v.plateState,
      vehicleType: v.vehicleType, notes: v.notes, npsdVehicle: v.npsdVehicle,
    }));
  }, [CC]);

  // ── Methods: Person/Vehicle linking ───────────────────────────────────

  const handleLinkPerson = useCallback((uuid: string) => {
    callCardLinkToPerson(uuid);
  }, []);

  const handleShowLinkedPerson = useCallback((npsdPersonId: number) => {
    const person = peopleStateRef.current.find((p: any) => p.npsdPerson === npsdPersonId);
    setPersonModalUuid(person ? person.uuid : null);
    setPersonModalId(npsdPersonId);
    setShowPersonModal(true);
  }, []);

  const handleCreateNpsdPerson = useCallback((uuid: string, personData: any) => {
    callCardAddNpsdPerson(uuid, personData);
  }, []);

  const handleLinkVehicle = useCallback((uuid: string) => {
    callCardLinkToVehicle(uuid);
  }, []);

  const handleShowLinkedVehicle = useCallback((npsdVehicleId: number) => {
    const vehicle = vehiclesStateRef.current.find((v: any) => v.npsdVehicle === npsdVehicleId);
    setVehicleModalUuid(vehicle ? vehicle.uuid : null);
    setVehicleModalId(npsdVehicleId);
    setShowVehicleModal(true);
  }, []);

  const handleCreateNpsdVehicle = useCallback((uuid: string, vehicleData: any) => {
    callCardAddNpsdVehicle(uuid, vehicleData);
  }, []);

  // ── Methods: Dropdown changes ─────────────────────────────────────────

  // Flash deferral: when the multiselect popover is open, defer the flash until
  // it closes so the highlight isn't hidden behind the dropdown.
  // Also track the priority at popover-open time so net-zero changes (e.g. add
  // then remove the same NOC) don't fire a misleading flash on close.
  const isMultiSelectOpenRef = useRef(false);
  const pendingPriorityFlashRef = useRef(false);
  const priorityAtMultiSelectOpenRef = useRef<number | null>(null);

  const onNatureOfCallOpen = useCallback(() => {
    isMultiSelectOpenRef.current = true;
    priorityAtMultiSelectOpenRef.current = typeof CC.priority === 'number' ? CC.priority : DEFAULT_CALL_CARD_PRIORITY;
  }, [CC]);

  const onNatureOfCallClose = useCallback(() => {
    isMultiSelectOpenRef.current = false;
    if (pendingPriorityFlashRef.current) {
      pendingPriorityFlashRef.current = false;
      const baseline = priorityAtMultiSelectOpenRef.current;
      const currentPriority = typeof CC.priority === 'number' ? CC.priority : DEFAULT_CALL_CARD_PRIORITY;
      if (baseline === null || currentPriority !== baseline) {
        setPriorityAutoChangeVersion((v) => v + 1);
      }
    }
    priorityAtMultiSelectOpenRef.current = null;
  }, [CC]);

  const triggerPriorityFlash = useCallback(() => {
    if (isMultiSelectOpenRef.current) {
      pendingPriorityFlashRef.current = true;
    } else {
      setPriorityAutoChangeVersion((v) => v + 1);
    }
  }, []);

  const applyNatureOfCallChange = useCallback((values: any[], items: any[]) => {
    CC.natureOfCall = values as any;
    fnUpdateCallCardNatureOfCallSopLink(items);
  }, [CC, fnUpdateCallCardNatureOfCallSopLink]);

  const onNatureOfCallChange = useCallback((e: { values: any[]; items: any[] }) => {
    const options = natureOfCallOptionsRef.current || [];
    const priorityByCode: Record<number, number | null> = {};
    for (const opt of options) {
      priorityByCode[opt.code] = (opt.priority !== undefined) ? opt.priority : null;
    }
    const oldCodes: number[] = (CC.natureOfCall as any) || [];
    const oldSet = new Set<number>(oldCodes);
    const newSet = new Set<number>(e.values);
    const addedCodes = [...newSet].filter((c) => !oldSet.has(c));
    const removedCodes = [...oldSet].filter((c) => !newSet.has(c));
    const currentPriority: number = typeof CC.priority === 'number' ? CC.priority : DEFAULT_CALL_CARD_PRIORITY;

    // Add-bump: if any added NOC has priority < current CC.priority, silently bump up.
    const addedPriorities = addedCodes
      .map((c) => priorityByCode[c])
      .filter((p): p is number => typeof p === 'number');
    const strongestAdded = addedPriorities.length ? Math.min(...addedPriorities) : null;
    if (strongestAdded !== null && strongestAdded < currentPriority) {
      CC.priority = strongestAdded;
      applyNatureOfCallChange(e.values, e.items);
      setRealtimeVersion((v) => v + 1);
      triggerPriorityFlash();
      return;
    }

    // Auto-lower: only when current priority was NOC-driven (matched a removed NOC).
    // If the dispatcher manually set priority above any NOC's level, leave it alone.
    if (removedCodes.length > 0) {
      const removedPriorities = removedCodes
        .map((c) => priorityByCode[c])
        .filter((p): p is number => typeof p === 'number');
      const strongestRemoved = removedPriorities.length ? Math.min(...removedPriorities) : null;
      if (strongestRemoved !== null && strongestRemoved === currentPriority) {
        const remainingPriorities = [...newSet]
          .map((c) => priorityByCode[c])
          .filter((p): p is number => typeof p === 'number');
        const strongestRemaining = remainingPriorities.length ? Math.min(...remainingPriorities) : null;
        const newPriority = strongestRemaining ?? DEFAULT_CALL_CARD_PRIORITY;
        if (newPriority !== currentPriority) {
          CC.priority = newPriority;
          applyNatureOfCallChange(e.values, e.items);
          setRealtimeVersion((v) => v + 1);
          triggerPriorityFlash();
          return;
        }
      }
    }

    applyNatureOfCallChange(e.values, e.items);
  }, [CC, applyNatureOfCallChange, triggerPriorityFlash]);

  const onClientChange = useCallback((e: { value: number | null; item: any }) => {
    if (!CC.client) {
      CC.client = { id: null, name: '', sites: [], allSites: [], clone: () => ({}) } as any;
    }
    const selectedId = e.value;
    CC.client.id = selectedId;

    if (e.item) {
      fnUpdateCallCardClientLink(e.item.id, e.item.name, e.item.companyId);
    } else {
      fnUpdateCallCardClientLink(null, null, null);
    }

    if (selectedId) {
      setSitesLoading(true);
      setSiteOptions([]);
      api.listClientGroupSite(selectedId, (result: any[]) => {
        setSiteOptions(result);
        setSitesLoading(false);
      }, () => { setSitesLoading(false); });
    } else {
      setSitesLoading(true);
      setSiteOptions([]);
      api.listAllClientGroupSite((result: any[]) => {
        setSiteOptions(result);
        setSitesLoading(false);
      }, () => { setSitesLoading(false); });
    }
  }, [CC, fnUpdateCallCardClientLink]);

  const onSiteChange = useCallback((e: { values: any[]; items: any[] }) => {
    if (!CC.client) {
      CC.client = { id: null, name: '', sites: [], allSites: [], clone: () => ({}) } as any;
    }
    CC.client.sites = e.values as any;

    if (e.items.length > 0 && !hasStringValue(CC.callLocFormattedAddress)) {
      const firstSite = e.items[0];
      if (firstSite.location) CC.callLocFormattedAddress = firstSite.location;
    }

    if (e.items.length > 0 && !CC.client.id) {
      const siteWithClient = e.items.find((s: any) => s.npsdClientId);
      if (siteWithClient) {
        // Link the client SYNCHRONOUSLY — the site item already carries npsdClientId. Setting it
        // only inside the list callback raced Send: a fast Send serialized client
        // {id: null, sites: [...]}, which the backend rejects ("Client cannot be null if Client
        // site is not null"). The list fetch below only refreshes the site options display.
        CC.client.id = siteWithClient.npsdClientId;
        const clientItem = clientOptionsRef.current.find((c: any) => c.id === siteWithClient.npsdClientId);
        if (clientItem) {
          fnUpdateCallCardClientLink(clientItem.id, clientItem.name, clientItem.companyId);
        }
        api.listClientGroupSite(siteWithClient.npsdClientId, (result: any[]) => {
          setSiteOptions(result);
        }, () => {});
      }
    }
  }, [CC, fnUpdateCallCardClientLink]);

  const onDispositionChange = useCallback((e: { value: any }) => {
    const dispositionCode = parseInt(e.value);
    CC.callDisposition = (!isNaN(dispositionCode) && dispositionCode !== -1) ? dispositionCode : null;
  }, [CC]);

  // ── Methods: Call card field changes ───────────────────────────────────

  const handleDetailsFieldChange = useCallback((data: { field: string; value: any }) => {
    if (data.field && CC) {
      (CC as any)[data.field] = data.value;
    }
  }, [CC]);

  const handleDispositionFieldChange = useCallback((data: { field: string; value: any }) => {
    if (data.field && CC) {
      (CC as any)[data.field] = data.value;
    }
  }, [CC]);

  // ── Methods: Comments ─────────────────────────────────────────────────

  const handleCommentAddedToNew = useCallback((commentData: any) => {
    addCallCommentHelper(new CallComment(commentData));
    CC.callCommentsToAttach.push(commentData.id);
  }, [CC]);

  const handleCommentUpdated = useCallback((data: { id: number; entry: string }) => {
    updateCallCommentEntry(data.id, data.entry);
  }, []);

  // ── Methods: CAD actions ──────────────────────────────────────────────

  const sendCADCC = useCallback(() => {
    if (CCCntr.newCard) {
      CCCntr.ccCurrentAction = CCAction.ADD;
      CCCntr.saveOrUpdateOrCloseCallCard();
    }
  }, [CCCntr]);

  const updateCC = useCallback(() => {
    CCCntr.ccCurrentAction = CCAction.UPDATE;
    CCCntr.saveOrUpdateOrCloseCallCard();
  }, [CCCntr]);

  const closeCC = useCallback(() => {
    CCCntr.closeCC();
  }, [CCCntr]);

  const editClosedCC = useCallback(() => {
    if (moduleType === ModuleType.mdt) {
      App.showPopupNotification('Please edit closed calls from the Dispatch module.', 'error');
      return;
    }
    const doEdit = () => {
      if (moduleType === ModuleType.cad) {
        CCCntr.setCADCallCardEnabled(true);
      }
      setIsEditingClosedCallCard(true);
    };
    const clientId = CC.client.id;
    dialogCheckIfUserWantsToEditOrReopenCallCardWithClientNotifications(clientId, doEdit);
  }, [CC, CCCntr, moduleType]);

  const recloseCC = useCallback(() => {
    CCCntr.ccCurrentAction = CCAction.RECLOSE;
    CC.updateAndReclose = true;
    CCCntr.saveOrUpdateOrCloseCallCard();
  }, [CC, CCCntr]);

  const cancelRecloseCC = useCallback(() => {
    refreshPage();
  }, []);

  const reopenClosedCC = useCallback(() => {
    if (moduleType === ModuleType.mdt) {
      App.showPopupNotification('Please reopen closed calls from the Dispatch module.', 'error');
      return;
    }
    const doReopen = () => {
      api.reopenCallCard(CC.id, () => {
        refreshPage();
      }, () => {});
    };
    const clientId = CC.client.id;
    dialogCheckIfUserWantsToEditOrReopenCallCardWithClientNotifications(clientId, doReopen);
  }, [CC, moduleType]);

  // ── Methods: MDT actions ──────────────────────────────────────────────

  const editMDTCC = useCallback(() => {
    setIsEditMode(true);
    CCCntr.setMDTCallCardEnabled(true);
  }, [CCCntr]);

  const cancelMDTCC = useCallback(() => {
    setIsEditMode(false);
    CCCntr.setMDTCallCardEnabled(false);
  }, [CCCntr]);

  const addSelfToCallCard = useCallback(() => {
    addSelfToCallCardMDT();
  }, []);

  // ── Methods: Officer time buttons ─────────────────────────────────────

  const EnRoute = useCallback(() => {
    if (currentUserHasPermission(AccessAuthorities.ROLE_MDT_UPDATE_RESPONSE_TIMES)) {
      const currentUserId = JSON.parse(localStorage.AuthToken).user_id;
      const currentOfficerAssigned = officersStateRef.current.slice().reverse().find((o: any) => o.officerId === currentUserId);
      setTimeUpdateModal({
        open: true, type: 'enroute',
        title: 'Select En-route Time', message: 'Please select your enroute time:',
        initialValue: currentOfficerAssigned?.enroute ? new Date(currentOfficerAssigned.enroute) : new Date(),
      });
    } else {
      setCallCardRealtimeEnabled(false);
      api.mdtEnrouteOnSceneOrClearedNow('enroute', CC.id.toString(), () => {
        refreshPage();
      }, () => {
        setCallCardRealtimeEnabled(true);
      });
    }
  }, [CC]);

  const OnScene = useCallback(() => {
    if (currentUserHasPermission(AccessAuthorities.ROLE_MDT_UPDATE_RESPONSE_TIMES)) {
      const currentUserId = JSON.parse(localStorage.AuthToken).user_id;
      const currentOfficerAssigned = officersStateRef.current.slice().reverse().find((o: any) => o.officerId === currentUserId);
      setTimeUpdateModal({
        open: true, type: 'onScene',
        title: 'Select On Scene Time', message: 'Please select your on scene time:',
        initialValue: currentOfficerAssigned?.onScene ? new Date(currentOfficerAssigned.onScene) : new Date(),
      });
    } else {
      setCallCardRealtimeEnabled(false);
      api.mdtEnrouteOnSceneOrClearedNow('onscene', CC.id.toString(), () => {
        refreshPage();
      }, () => {
        setCallCardRealtimeEnabled(true);
      });
    }
  }, [CC]);

  const Cleared = useCallback(() => {
    if (currentUserHasPermission(AccessAuthorities.ROLE_MDT_UPDATE_RESPONSE_TIMES)) {
      const currentUserId = JSON.parse(localStorage.AuthToken).user_id;
      const currentOfficerAssigned = officersStateRef.current.slice().reverse().find((o: any) => o.officerId === currentUserId);
      setTimeUpdateModal({
        open: true, type: 'cleared',
        title: 'Select Cleared Time', message: 'Please select your cleared time:',
        initialValue: currentOfficerAssigned?.cleared ? new Date(currentOfficerAssigned.cleared) : new Date(),
      });
    } else {
      setCallCardRealtimeEnabled(false);
      const callCardId = CC.id;
      api.mdtEnrouteOnSceneOrClearedNow('cleared', CC.id.toString(), () => {
        removeMDTActiveCallCard(callCardId);
        refreshPage();
        setShowingRecentOfficerAssignedDialogOnPageLoaded(true);
      }, () => {
        setCallCardRealtimeEnabled(true);
      });
    }
  }, [CC]);

  const handleTimeUpdateConfirm = useCallback((selectedDateTime: Date | null) => {
    const updateTimeType = timeUpdateModal.type;
    if (!updateTimeType || !selectedDateTime) {
      setTimeUpdateModal({ open: false, type: '', title: '', message: '', initialValue: null });
      return;
    }

    let updateTimeData: any;
    let updateTypeInURL: string = '';
    const isoDateTime = selectedDateTime.toISOString();

    if (updateTimeType === 'enroute') {
      updateTimeData = { enroute: isoDateTime };
      updateTypeInURL = 'enroute';
    } else if (updateTimeType === 'onScene') {
      updateTimeData = { onScene: isoDateTime };
      updateTypeInURL = 'onscene';
    } else if (updateTimeType === 'cleared') {
      updateTimeData = { cleared: isoDateTime };
      updateTypeInURL = 'cleared';
    }

    setTimeUpdateModal({ open: false, type: '', title: '', message: '', initialValue: null });
    setCallCardRealtimeEnabled(false);
    const callCardId = CC.id;

    api.mdtActions(updateTypeInURL, CC.id.toString(), JSON.stringify(updateTimeData), () => {
      if (updateTimeType === 'cleared') {
        removeMDTActiveCallCard(callCardId);
        setShowingRecentOfficerAssignedDialogOnPageLoaded(true);
      }
      refreshPage();
    }, () => {
      setCallCardRealtimeEnabled(true);
    });
  }, [timeUpdateModal.type, CC]);

  const handleTimeUpdateCancel = useCallback(() => {
    setTimeUpdateModal({ open: false, type: '', title: '', message: '', initialValue: null });
  }, []);

  // ── Methods: Case report ──────────────────────────────────────────────

  const PrimaryOfficerClearRepReq = useCallback(() => {
    const data = {
      id: CC.id,
      natureOfCall: CC.natureOfCall,
      natureOfCallValues: natureOfCallOptionsRef.current
        .filter((item: any) => CC.natureOfCall && (CC.natureOfCall as any[]).indexOf(item.code) !== -1)
        .map((item: any) => item.value),
      callDateTime: CC.callDateTime,
      primaryOfficer: CC.primaryOfficer,
      primaryDispatched: CC.primaryDispatched,
      primaryEnroute: CC.primaryEnroute,
      primaryOnScene: CC.primaryOnScene,
      primaryCleared: CC.primaryCleared,
      backupUnits: CC.backupUnits,
      callLocFormattedAddress: CC.callLocFormattedAddress,
      callLocAddress2: CC.callLocAddress2,
      callLocName: CC.callLocName,
      callDisposition: CC.callDisposition,
      callDispositionValue: CC.callDispositionValue,
      dispositionNotes: CC.dispositionNotes,
      clientIdAndSiteIds: hasValue(CC.client.id) ? { clientId: CC.client.id, siteIds: CC.client.sites } : null,
      peopleIds: CC.people.map((ccPerson: any) => ccPerson.npsdPerson).filter(Number),
      vehicleIds: CC.vehicles.map((ccVehicle: any) => ccVehicle.npsdVehicle).filter(Number),
    };
    localStorage.setItem('ReportFromCallCard', JSON.stringify(data));
    showNewCaseReport();
  }, [CC]);

  const PrimaryOfficerClearRepReqWithPrompt = useCallback(() => {
    setShowCreateCaseReportModal(true);
  }, []);

  const confirmCreateCaseReport = useCallback(() => {
    setShowCreateCaseReportModal(false);
    PrimaryOfficerClearRepReq();
  }, [PrimaryOfficerClearRepReq]);

  const cancelCreateCaseReport = useCallback(() => {
    setShowCreateCaseReportModal(false);
  }, []);

  // ── Methods: Downloads ────────────────────────────────────────────────

  const downloadCCPdf = useCallback(() => {
    const callCardId = CC.id;
    const timeZoneId = moment.tz.guess();
    App.showPopupNotification('Preparing Call Card PDF for download. Please wait.', 'success');
    api.callCardPdf(callCardId, timeZoneId, (result: any) => {
      const pdfBase64String = result.pdfBase64;
      const filename = result.pdfFilename;
      if (isUWPApp || isIOSApp || isAndroidApp || isWindowsAppSdkApp) {
        notifyApp('activityReportPDFBase64', [filename, pdfBase64String]);
      } else {
        const blob = b64toBlob(pdfBase64String, 'application/pdf');
        saveAs(blob, filename);
      }
    }, () => {
      App.showPopupNotification('Failed to download Call Card PDF.', 'error', 30);
    });
  }, [CC]);

  // ── Methods: Map ──────────────────────────────────────────────────────

  const ViewMap = useCallback(() => {
    navigateToMapByCallCardId(CC.id);
  }, [CC]);

  // ── Methods: Client address ───────────────────────────────────────────

  const updateClientAddressFromServer = useCallback((onComplete?: (data: any) => void) => {
    if (hasValue(CC) && hasValue(CC.client) && hasValue(CC.client.id)) {
      api.getNPSDClientAddress(CC.client.id, (addressInformation: any) => {
        if (hasValue(CC)) {
          CC.callLocFormattedAddress = addressInformation.locFormattedAddress;
          CC.callLocAddress2 = addressInformation.locAddress2;
          CC.callLocName = addressInformation.locName;
          onComplete?.(addressInformation);
        }
      });
    } else {
      App.showPopupNotification('Set call location to address of selected client.', 'warning');
    }
  }, [CC]);

  // ── Methods: Navigation ───────────────────────────────────────────────

  const turnByTurnNavigationV2 = useCallback(() => {
    // An approximate or service-area pin would route the officer to the wrong place.
    const pinIsUnreliable = CC.addressVerificationStatus === 'LOW' || CC.addressVerificationStatus === 'AREA';
    const addressText = hasStringValue(CC.callLocFormattedAddress)
      ? CC.callLocFormattedAddress
      : [CC.callLocAddress, CC.callLocCity, CC.callLocState, CC.callLocZip].filter((p: string) => hasStringValue(p)).join(', ');
    startTurnByTurnNavigationV2(CC.latitude, CC.longitude, pinIsUnreliable && hasStringValue(addressText) ? addressText : null);
  }, [CC]);

  const navigationSettings = useCallback(() => {
    setSelectedNavigationType(getOrnaNavigationTypeWithDefault());
    setShowNavigationSettingsModal(true);
  }, []);

  const closeNavigationSettingsModal = useCallback(() => {
    setShowNavigationSettingsModal(false);
    setSavingNavigationSettings(false);
  }, []);

  const saveNavigationSettings = useCallback(() => {
    setSavingNavigationSettings(true);
    const navType = Number(selectedNavigationType);
    api.updatePersonSettings({ navigationType: navType }, () => {
      setLocalOrnaNavigationType(navType);
      const authObj = getAuthObject();
      if (authObj) {
        if (!authObj.personSettings) authObj.personSettings = {};
        authObj.personSettings.navigationType = navType;
        setAuthObject(authObj);
      }
      setShowNavigationSettingsModal(false);
      setSavingNavigationSettings(false);
      App.showPopupNotification('Navigation settings saved.', 'success');
    }, () => {
      App.showPopupNotification('Error saving settings.', 'error');
      setSavingNavigationSettings(false);
    });
  }, [selectedNavigationType]);

  // ── Methods: Close call modal ─────────────────────────────────────────

  const openCloseCallModal = useCallback(() => {
    setShowCloseCallModal(true);
  }, []);

  const handleCloseCallConfirm = useCallback((data: { disposition: number | null; dispositionNotes: string }) => {
    CC.callDisposition = data.disposition;
    CC.dispositionNotes = data.dispositionNotes;
    setShowCloseCallModal(false);
    CCCntr.closeCC();
  }, [CC, CCCntr]);

  // ── Methods: SOP modal ────────────────────────────────────────────────

  const openSopModal = useCallback(() => {
    const nocData = natureOfCallTempSopData;
    if (!hasValue(nocData)) return;
    const nocDataWithSop = nocData.filter((e: any) => e.hasSop);
    const codesWithSop = nocDataWithSop.map((e: any) => e.code);

    const data = { natureOfCallCodes: codesWithSop, companyId: getCurrentCompanyId() };

    setSopModalLoading(true);
    setSopModalContent('');
    setShowSopModal(true);

    api.getStandardOperatingProcedures(data, (result: any) => {
      loadSopContent(result, nocDataWithSop);
    }, () => {
      setSopModalLoading(false);
      setShowSopModal(false);
      App.showPopupNotification('Error getting standard operating procedures data.', 'error');
    });
  }, [natureOfCallTempSopData]);

  const loadSopContent = useCallback((sopInfoList: any[], nocDataWithSop: any[]) => {
    const companyId = getCurrentCompanyId();
    const imageLoadPromises = sopInfoList.map((sopInfo: any) => {
      return new Promise<string>((resolve) => {
        const nocObj = getObjectInArrayByProperty(nocDataWithSop, 'code', sopInfo.natureOfCallCode);
        loadNocSopImages(sopInfo.sop, sopInfo.natureOfCallCode, companyId, (processedSop: string) => {
          const htmlContent = `<div class="card card-primary"><div class="k-header k-grid-toolbar">${nocObj.value}</div><div class="card-body"><div class="card-block sop-card-block">${processedSop}</div></div></div>`;
          resolve(htmlContent);
        });
      });
    });
    Promise.all(imageLoadPromises).then((htmlContentArray) => {
      setSopModalContent(htmlContentArray.join(''));
      setSopModalLoading(false);
    });
  }, []);

  const closeSopModal = useCallback(() => {
    setShowSopModal(false);
    setSopModalContent('');
    setSopModalLoading(false);
  }, []);

  // ── Methods: Page modals (Client, Person, Vehicle, Map) ───────────────

  const openClientModal = useCallback((clientId: string) => {
    setClientModalClientId(clientId);
    setShowClientModal(true);
  }, []);

  const closeClientModal = useCallback(() => {
    setShowClientModal(false);
    setClientModalClientId('');
  }, []);

  const handleClientUpdatedInModal = useCallback((result: any) => {
    // Update the client link text with new name if available
    if (result && CC) {
      const name = result.name || result.groupName || '';
      if (name) {
        const id = CC.client?.id || clientModalClientId;
        setClientLink(` <a href="javascript:void(0)" onclick="App.callCardController.openClientModal('${id}')" title='${name}' style='font-size:13px;line-height:1.42857143'>View/Edit Client</a>`);
      }
    }
  }, [CC, clientModalClientId]);

  const openMapModal = useCallback(() => {
    setMapModalAddress(CC.callLocFormattedAddress);
    setShowMapModal(true);
  }, [CC]);

  const closeMapModal = useCallback(() => {
    setShowMapModal(false);
    setMapModalAddress('');
  }, []);

  const openPersonModal = useCallback((npsdPersonId: number) => {
    const person = peopleStateRef.current.find((p: any) => p.npsdPerson === npsdPersonId);
    setPersonModalUuid(person ? person.uuid : null);
    setPersonModalId(npsdPersonId);
    setShowPersonModal(true);
  }, []);

  const handlePersonUpdatedInModal = useCallback((result: any) => {
    if (!personModalUuid || !result) return;
    const people = peopleStateRef.current;
    const idx = people.findIndex((p: any) => p.uuid === personModalUuid);
    if (idx !== -1) {
      const person = people[idx];
      person.firstName = result.firstName ?? person.firstName;
      person.lastName = result.lastName ?? person.lastName;
      person.dob = result.dob ?? person.dob;
      person.license = result.driversLicenseNum ?? person.license;
      person.notes = result.notes ?? person.notes;
      setPeople(people.slice());
    }
  }, [personModalUuid]);

  const closePersonModal = useCallback(() => {
    setShowPersonModal(false);
    setPersonModalId(null);
    setPersonModalUuid(null);
  }, []);

  const openVehicleModal = useCallback((npsdVehicleId: number) => {
    const vehicle = vehiclesStateRef.current.find((v: any) => v.npsdVehicle === npsdVehicleId);
    setVehicleModalUuid(vehicle ? vehicle.uuid : null);
    setVehicleModalId(npsdVehicleId);
    setShowVehicleModal(true);
  }, []);

  const handleVehicleUpdatedInModal = useCallback((result: any) => {
    if (!vehicleModalUuid || !result) return;
    const vehicles = vehiclesStateRef.current;
    const idx = vehicles.findIndex((v: any) => v.uuid === vehicleModalUuid);
    if (idx !== -1) {
      const vehicle = vehicles[idx];
      vehicle.make = result.make ?? vehicle.make;
      vehicle.model = result.model ?? vehicle.model;
      vehicle.color = result.color ?? vehicle.color;
      vehicle.year = result.year ?? vehicle.year;
      vehicle.plateNumber = result.plateNumber ?? vehicle.plateNumber;
      vehicle.plateState = result.plateState ?? vehicle.plateState;
      vehicle.notes = result.notes ?? vehicle.notes;
      setVehicles(vehicles.slice());
    }
  }, [vehicleModalUuid]);

  const closeVehicleModal = useCallback(() => {
    setShowVehicleModal(false);
    setVehicleModalId(null);
    setVehicleModalUuid(null);
  }, []);

  // ── Methods: Connections search modal ─────────────────────────────────

  const openConnectionsSearchModal = useCallback((npsdType: string, callback: Function, gridDataItem: any) => {
    let initValues: Record<string, any> = {};
    if (gridDataItem && npsdType === 'Person') {
      initValues = {
        lastName: gridDataItem.lastName || '',
        firstName: gridDataItem.firstName || '',
        dob: gridDataItem.dob || null,
        driversLicenseNum: gridDataItem.license || '',
      };
    } else if (gridDataItem && npsdType === 'Vehicle') {
      initValues = {
        make: gridDataItem.make || '',
        model: gridDataItem.model || '',
        color: gridDataItem.color || '',
        plateNumber: gridDataItem.plateNumber || '',
        plateState: gridDataItem.plateState || '',
      };
    }
    setConnectionsSearch({ open: true, type: npsdType, initialValues: initValues, callback });
  }, []);

  const handleConnectionsSearchView = useCallback((item: any) => {
    const type = connectionsSearchRef.current.type.toLowerCase();
    if (type === 'person') {
      setPersonModalId(item.id);
      setPersonModalUuid(null);
      setShowPersonModal(true);
    } else if (type === 'vehicle') {
      setVehicleModalId(item.id);
      setVehicleModalUuid(null);
      setShowVehicleModal(true);
    }
  }, []);

  const handleConnectionsSearchSelect = useCallback((item: any) => {
    const type = connectionsSearchRef.current.type.toLowerCase();
    const cb = connectionsSearchRef.current.callback;

    if (type === 'person') {
      api.getNPSDItemById(String(item.id), 'person', (result: any) => {
        const dataItem = {
          id: item.id,
          firstName: result.firstName,
          lastName: result.lastName,
          dob: result.dob,
          driversLicenseNum: result.driversLicenseNum,
          notes: result.notes,
          npsdPerson: result.npsdPerson,
        };
        if (cb) cb('lnkCallCardLinkPerson', dataItem);
        setConnectionsSearch({ open: false, type: '', initialValues: {}, callback: null });
      });
    } else if (type === 'vehicle') {
      api.getNPSDItemById(String(item.id), 'vehicle', (result: any) => {
        const dataItem = new CCVehicle({
          id: item.id, make: result.make, model: result.model, year: result.year,
          color: result.color, plateState: result.plateState, plateNumber: result.plateNumber,
          vehicleType: result.vehicleType, notes: result.notes, npsdVehicle: result.npsdVehicle,
        });
        if (cb) cb('lnkCallCardLinkVehicle', dataItem);
        setConnectionsSearch({ open: false, type: '', initialValues: {}, callback: null });
      });
    }
  }, []);

  const closeConnectionsSearchModal = useCallback(() => {
    setConnectionsSearch({ open: false, type: '', initialValues: {}, callback: null });
  }, []);

  // ── Methods: Attachments ──────────────────────────────────────────────

  const handleAttachmentUpload = useCallback((fileInput: HTMLInputElement) => {
    const numAttachmentsBeforeAdding = CC.attachmentList.length;
    App.uploadFromFileInput(fileInput, {
      successCallback: (idx: number, attachment: Attachment) => {
        const target = CC?.attachmentList?.[numAttachmentsBeforeAdding + idx];
        if (target) {
          target.signedURL = attachment.signedURL + '#' + new Date().getTime();
          // Trigger re-render so preview image updates with signed URL
          setAttachments(CC.attachmentList.slice());
        }
      },
      updateAttachmentInfo: (idx: number, attachmentInfo: AttachmentInfo) => {
        CC.attachmentInfoList.push(attachmentInfo);
      },
      updateAttachment: (idx: number, attachment: Attachment) => {
        CC.attachmentList.push(attachment);
        setAttachments(CC.attachmentList.slice());
      },
    });
  }, [CC]);

  const removeAttachment = useCallback((attachmentClicked: any) => {
    removeObjectFromArrayByProperty(CC.attachmentList, 'id', attachmentClicked.id);
    removeObjectFromArrayByProperty(CC.attachmentInfoList, 'id', attachmentClicked.id);
    setAttachments(CC.attachmentList.slice());
  }, [CC]);

  // ── Methods: Custom location ──────────────────────────────────────────

  const openAddCustomLocationDialog = useCallback((coordinates: string) => {
    setAddCustomLocationData({ location: '', coordinates });
    setShowAddCustomLocationModal(true);
  }, []);

  const handleSaveCustomLocation = useCallback((data: { location: string; latitude: number; longitude: number }) => {
    setSavingCustomLocation(true);
    const savedLocation = data.location;
    api.saveCustomLocation(data, () => {
      setSavingCustomLocation(false);
      setShowAddCustomLocationModal(false);
      CC.callLocFormattedAddress = savedLocation;
      setAddCustomLocationData({ location: '', coordinates: '' });
    }, () => {
      setSavingCustomLocation(false);
      App.showPopupNotification('Failed to save custom location.', 'error');
    });
  }, [CC]);

  const cancelAddCustomLocationDialog = useCallback(() => {
    setShowAddCustomLocationModal(false);
    setAddCustomLocationData({ location: '', coordinates: '' });
  }, []);

  // ── Methods: Client match modal ───────────────────────────────────────

  const confirmClientMatch = useCallback(() => {
    const client = clientMatchModal.clientData;
    const pendingAction = clientMatchModal.pendingAction;
    setClientMatchModal({ open: false, clientName: '', clientData: null, pendingAction: null });
    // Replace CC.client with a new object so React detects the change
    CC.client = { ...CC.client, id: client.id, name: client.name, sites: [] };
    fnUpdateCallCardClientLink(client.id, client.name, null);
    setSitesLoading(true);
    setSiteOptions([]);
    api.listClientGroupSite(client.id, (result: any[]) => {
      setSiteOptions(result);
      setSitesLoading(false);
    }, () => { setSitesLoading(false); });
    updateClientAddressFromServer();
    if (pendingAction) pendingAction();
  }, [clientMatchModal, CC, fnUpdateCallCardClientLink, updateClientAddressFromServer]);

  const closeClientMatchModal = useCallback(() => {
    const pendingAction = clientMatchModal.pendingAction;
    setClientMatchModal({ open: false, clientName: '', clientData: null, pendingAction: null });
    if (pendingAction) pendingAction();
  }, [clientMatchModal]);

  // ── Methods: Location blur (client match suggestion) ──────────────────

  const showDialogIfLocationIsClientLocation = useCallback((event: any) => {
    if (!CC) return;
    const callLocation = CC.callLocFormattedAddress;
    if (!hasStringValue(callLocation)) return;

    let pendingAction: Function | null = null;
    const related = event && event.relatedTarget;
    if (related && (related.matches('input[type="button"], button, .kstc-btn'))) {
      pendingAction = () => { related.click(); };
    }

    const callLocationNoCommas = callLocation.replace(/,/g, '');
    const clientList = clientOptionsRef.current || [];
    const clientListNoCommas = clientList.map((client: any) => {
      if (hasStringValue(client.locFormattedAddress)) {
        return { id: client.id, name: client.name, locFormattedAddress: client.locFormattedAddress.replace(/,/g, '') };
      } else {
        return { id: client.id, name: client.name, locFormattedAddress: '' };
      }
    });

    const currentClientId = CC.client ? CC.client.id : null;
    if (callLocationNoCommas && callLocationNoCommas.length >= 4 && !currentClientId) {
      const options = {
        keys: [{ name: 'locFormattedAddress', weight: 1.0 }],
        threshold: 0.05, isCaseSensitive: false, limit: 1,
      };
      const fuse = new Fuse(clientListNoCommas, options);
      const results = fuse.search(callLocationNoCommas);
      let clientAtTypedLocation: any = null;
      if (results.length) {
        clientAtTypedLocation = results[0].item;
      }
      if (clientAtTypedLocation) {
        setClientMatchModal({
          open: true,
          clientName: clientAtTypedLocation.name,
          clientData: clientAtTypedLocation,
          pendingAction,
        });
        return;
      }
    }
  }, [CC]);

  // ── Methods: Delete call ──────────────────────────────────────────────

  const confirmDeleteCall = useCallback(() => {
    setShowDeleteCallModal(false);
    navigateToNewCallCard();
  }, []);

  // ── Effect: Initialize (mount) ────────────────────────────────────────

  useEffect(() => {
    // Initialize officer data
    const initialOfficers = getOfficerListFromCallCard(CC);
    setOfficers(convertOfficersForGrid(initialOfficers));
    setAvailablePeople(CCCntr.cachedOfficerList || []);

    // Initialize people, vehicles, and attachments
    setPeople(convertPeopleForGrid(CC.people));
    setVehicles(convertVehiclesForGrid(CC.vehicles));
    setAttachments(CC.attachmentList || []);

    // Set initial client values
    const initClientId = CC.client !== null ? CC.client.id : null;
    const initClientAllSites = (CC.client !== null && CC.client.allSites !== null) ? CC.client.allSites : [];
    setInitialClientId(initClientId);
    setInitialClientAllSites(initClientAllSites);

    // Load Nature of Call options
    api.listNatureOfCall((result: any) => {
      if (!CC || !App.callCardController) return;
      const data = mergeCurrentNatureOfCallIntoNatureOfCallList(
        CC.natureOfCall, CC.natureOfCallValues, result.natureOfCallList
      );
      // Merged-in items (NOCs on the call but no longer in the list) need company names too.
      addCompanyNameField(data, result.companyList);
      setNatureOfCallOptions(data);
      const selectedItems = data.filter((item: any) =>
        CC.natureOfCall && (CC.natureOfCall as any[]).indexOf(item.code) !== -1
      );
      fnUpdateCallCardNatureOfCallSopLink(selectedItems);
    }, () => {
      App.showPopupNotification('Error getting nature of call data.', 'error');
    });

    // Load Client options
    api.listClientGroup((result: any) => {
      if (!App.callCardController) return;
      const list = result.npsdClientList.map((item: any) => {
        const company = result.companyList.find((n: any) => n.id === item.companyId);
        return { ...item, name: item.name + ' - ' + (company ? company.name : '') };
      });
      list.sort((a: any, b: any) => a.name.localeCompare(b.name));
      setClientOptions(list);
      if (initClientId) {
        const selectedClient = list.find((c: any) => c.id === initClientId);
        if (selectedClient) {
          fnUpdateCallCardClientLink(selectedClient.id, selectedClient.name, selectedClient.companyId);
        }
      }
    });

    // Load Site options (always fetch from API to get full site data including location)
    if (initClientId) {
      api.listClientGroupSite(initClientId, (result: any[]) => {
        setSiteOptions(result);
      }, () => {});
    } else {
      api.listAllClientGroupSite((result: any[]) => {
        setSiteOptions(result);
      }, () => {});
    }

    // Load available officers
    api.getAvailableCADOfficers(CC.id, (result: any) => {
      if (!CCCntr) return;
      processCallCardAvailableOfficers(result);
      CCCntr.cachedOfficerList = result.officerList;
      setAvailablePeople(result.officerList);
    });

    // Load Disposition options
    api.listCallDispositions((result: any) => {
      if (!CC || !App.callCardController) return;
      const data = mergeCurrentCallDispositionIntoCallDispositionList(
        CC.callDisposition, CC.callDispositionValue, result.callDispositionList
      );
      addCompanyNameField(data, result.companyList);
      // Placeholder gets no companyId/companyName so it stays ungrouped at the top
      // and doesn't count as a cross-company item.
      data.unshift({ dispositionCode: -1, dispositionValue: 'Select Disposition', companyId: null, companyName: '', listIdx: -1 });
      setDispositionOptions(data);
    }, () => {
      App.showPopupNotification('Error getting call disposition data.', 'error');
    });

    // Handle closed call state
    if (CC.ccStatus === CallCardStatus.CASE_CLOSED) {
      setCallCardNotClosed(false);
    } else {
      setCallCardNotClosed(true);
    }

    // MDT-specific initialization
    if (moduleType === ModuleType.mdt) {
      // Self-initiated call detection
      const selfInitiatedCallCardId = localStorage.getItem('SelfInitiatedCallCard');
      localStorage.removeItem('SelfInitiatedCallCard');
      if (CC.id && selfInitiatedCallCardId === CC.id.toString()) {
        setIsEditMode(true);
      }

      // Check if user is officer on this call card
      const userId = getCurrentUserId().toString();
      let isOfficer = false;
      if (CC.primaryOfficer !== null && CC.primaryOfficer !== undefined && CC.primaryOfficer.toString() === userId) {
        isOfficer = true;
      }
      for (const backupUnit of CC.backupUnits) {
        if (backupUnit.officer.toString() === userId) isOfficer = true;
      }
      if (isOfficer) setIsOfficerOnCallCard(true);

      // Inline the logic from updateMDTEditModeAllowed() because App.callCardController
      // shim isn't set up yet (its useEffect runs after this one).
      if (CC.ccStatus === CallCardStatus.CASE_CLOSED || !mdtHasPermissionsToEditCallCard()) {
        setEditModeAllowed(false);
        setIsEditMode(false);
      } else {
        setEditModeAllowed(true);
        // If user preference is to open calls in edit mode by default, enable it
        // (unless already set by self-initiated call detection above)
        const authObj = getAuthObject();
        if (authObj?.personSettings?.isMdtCallCardEditModeDefault) {
          setIsEditMode(true);
        }
      }
    }

    // Set page as ready
    setPageReady(true);

    // Load patrol zone names
    if (hasValue(CC.latitude) && hasValue(CC.longitude)) {
      getPatrolZoneNames(CC.latitude, CC.longitude, (result: string[]) => {
        setPatrolZoneNames(result);
      });
    }

    // Load call alert
    if (hasStringValue(CC.callLocFormattedAddress)) {
      const data = { locFormattedAddress: CC.callLocFormattedAddress };
      api.getCallAlert(data, (callAlertResult: any) => {
        if (hasStringValue(callAlertResult.message)) {
          setCallAlert(callAlertResult);
        }
      }, () => {
        if (getAuthObject()) {
          App.showPopupNotification('Error getting call alert.', 'error');
        }
      });
    }

    // Preload Custom Locations list
    getCustomLocationListPromise();
  }, []); // Run once on mount

  // ── Effect: Window resize ─────────────────────────────────────────────

  useEffect(() => {
    let rafId: number | null = null;
    const throttledResize = () => {
      if (rafId) return; // already scheduled
      rafId = requestAnimationFrame(() => {
        rafId = null;
        calculateVisibleTabs();
      });
    };
    window.addEventListener('resize', throttledResize);
    return () => {
      window.removeEventListener('resize', throttledResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [calculateVisibleTabs]);

  // ── Effect: Click outside more menu ───────────────────────────────────

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const moreMenu = document.querySelector('.cc-tabs-more');
      if (moreMenu && !moreMenu.contains(event.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // ── Effect: Click on custom location link ─────────────────────────────

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('#lnkCallCardAddCustomLocation') as HTMLElement;
      if (!target) return;
      const coordinates = target.dataset.coords;
      if (coordinates) openAddCustomLocationDialog(coordinates);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openAddCustomLocationDialog]);

  // ── Effect: Calculate visible tabs on mount and when dependencies change

  useEffect(() => {
    // Show all tabs initially so they can be measured, then calculate overflow
    setVisibleTabIds([...availableTabIds]);
    tabWidthCacheRef.current.clear();
    // Give DOM time to render all tabs before measuring
    const timer = setTimeout(() => calculateVisibleTabs(), 100);
    return () => clearTimeout(timer);
  }, [availableTabIds, calculateVisibleTabs]);

  // ── Effect: Watch CC.id changes → recalculate tabs ────────────────────

  const prevCCIdRef = useRef(CC?.id);
  useEffect(() => {
    if (prevCCIdRef.current !== CC?.id) {
      prevCCIdRef.current = CC?.id;
      const timer = setTimeout(() => calculateVisibleTabs(), 50);
      return () => clearTimeout(timer);
    }
  }, [CC?.id, calculateVisibleTabs]);

  // ── Effect: Watch callCardNotClosed → recalculate tabs ────────────────

  useEffect(() => {
    const timer = setTimeout(() => calculateVisibleTabs(), 50);
    return () => clearTimeout(timer);
  }, [callCardNotClosed, calculateVisibleTabs]);

  // ── Effect: Clock timer ───────────────────────────────────────────────

  useEffect(() => {
    const interval = setInterval(() => {
      setClockTime(new Date().toLocaleTimeString());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // ── Effect: Expose CallCardController for external code ──────────────

  useEffect(() => {
    const controller: any = {
      // Data properties (formerly on $data)
      get CC() { return CC; },
      get CCCntr() { return CCCntr; },
      get officers() { return officersStateRef.current; },
      set officers(val: any) { setOfficers(val); },
      get availablePeople() { return availablePeople; },
      set availablePeople(val: any) { setAvailablePeople(val); },
      get people() { return peopleStateRef.current; },
      set people(val: any) { setPeople(val); },
      get vehicles() { return vehiclesStateRef.current; },
      set vehicles(val: any) { setVehicles(val); },
      get attachments() { return attachments; },
      set attachments(val: any) { setAttachments(val); },
      get natureOfCallOptions() { return natureOfCallOptionsRef.current; },
      set natureOfCallOptions(val: any) { setNatureOfCallOptions(val); },
      get clientOptions() { return clientOptionsRef.current; },
      set clientOptions(val: any) { setClientOptions(val); },
      get siteOptions() { return siteOptionsRef.current; },
      set siteOptions(val: any) { setSiteOptions(val); },
      get dispositionOptions() { return dispositionOptions; },
      set dispositionOptions(val: any) { setDispositionOptions(val); },
      get patrolZoneNames() { return patrolZoneNames; },
      set patrolZoneNames(val: any) { setPatrolZoneNames(val); },
      get callAlert() { return callAlert; },
      set callAlert(val: any) { setCallAlert(val); },
      get initialClientId() { return initialClientId; },
      set initialClientId(val: any) { setInitialClientId(val); },
      get initialClientAllSites() { return initialClientAllSites; },
      set initialClientAllSites(val: any) { setInitialClientAllSites(val); },

      // UI state properties
      get callCardNotClosed() { return callCardNotClosedRef.current; },
      set callCardNotClosed(val: boolean) { setCallCardNotClosed(val); },
      get isEditMode() { return isEditModeRef.current; },
      set isEditMode(val: boolean) { setIsEditMode(val); },
      get isEditingClosedCallCard() { return isEditingClosedCallCardRef.current; },
      set isEditingClosedCallCard(val: boolean) { setIsEditingClosedCallCard(val); },
      get isOfficerOnCallCard() { return isOfficerOnCallCard; },
      set isOfficerOnCallCard(val: boolean) { setIsOfficerOnCallCard(val); },
      get saveOrUpdateButtonEnabled() { return saveOrUpdateButtonEnabled; },
      set saveOrUpdateButtonEnabled(val: boolean) { setSaveOrUpdateButtonEnabled(val); },
      get pageReady() { return pageReady; },
      set pageReady(val: boolean) { setPageReady(val); },
      get clockTime() { return clockTimeRef.current; },
      set clockTime(val: string) { setClockTime(val); },
      get module() { return moduleType; },
      get editModeAllowed() { return editModeAllowed; },
      set editModeAllowed(val: boolean) { setEditModeAllowed(val); },
      get mdtOfficerTimeButtonsEnabled() { return mdtOfficerTimeButtonsEnabledRef.current; },
      get canEnterMDTEditMode() { return canEnterMDTEditModeRef.current; },
      get showPersonModal() { return showPersonModalRef.current; },
      get showVehicleModal() { return showVehicleModalRef.current; },
      get showClientModal() { return showClientModalRef.current; },
      get personModalUuid() { return personModalUuid; },
      get vehicleModalUuid() { return vehicleModalUuid; },

      // Methods
      openSopModal,
      openClientModal,
      openMapModal,
      openPersonModal,
      openVehicleModal,
      openConnectionsSearchModal,
      openAddCustomLocationDialog,
      fnUpdateCallCardClientLink,
      fnUpdateCallCardNatureOfCallSopLink,
      calculateVisibleTabs,
      updateCC,
      sendCADCC,
      editMDTCC,
      closePersonModal,
      closeVehicleModal,
      closeConnectionsSearchModal,
      forceSaveOfficerEdits: () => officersRef.current?.forceSaveAndCloseAllEdits?.(),
      forceUpdate: () => setRealtimeVersion(v => v + 1),
    };

    App.callCardController = controller;

    return () => {
      if (App.callCardController === controller) {
        App.callCardController = null;
      }
    };
  }, [
    CC, CCCntr, moduleType, availablePeople, dispositionOptions, patrolZoneNames,
    callAlert, initialClientId, initialClientAllSites, isOfficerOnCallCard,
    saveOrUpdateButtonEnabled, pageReady, editModeAllowed,
    personModalUuid, vehicleModalUuid,
    openSopModal, openClientModal, openMapModal, openPersonModal,
    openVehicleModal, openConnectionsSearchModal, openAddCustomLocationDialog,
    fnUpdateCallCardClientLink, fnUpdateCallCardNatureOfCallSopLink, calculateVisibleTabs,
    updateCC, sendCADCC, editMDTCC, closePersonModal, closeVehicleModal, closeConnectionsSearchModal,
  ]);

  // ── Derived values for rendering ──────────────────────────────────────

  const isNewCard = useMemo(() => CCCntr.newCard, [CCCntr]);

  // ── Render ────────────────────────────────────────────────────────────

  if (!pageReady) {
    return <div className={`call-card-screen ${theme}`}><div className="kst-loading"></div></div>;
  }

  return (
    <div className={`call-card-screen ${theme}`}>
      <form onSubmit={e => e.preventDefault()}>
        {/* Nav tabs + Action buttons */}
        <nav className="sub-navigation sub-navigation--callcard" id="myNavbar">
          <div className="cc-tabs-container">
            <ul id="CallCardScreenTabs" className="cc-tabs-list" role="tablist">
              {availableTabIds.map(tabId => (
                <li
                  key={tabId}
                  role="presentation"
                  className={`cc-tab ${activeTab === tabId ? 'active' : ''}`}
                  data-tab-id={tabId}
                  style={!visibleTabIds.includes(tabId) ? { display: 'none' } : undefined}
                >
                  <a href={`#CallCardTab${tabId}`} role="tab" onClick={(e) => { e.preventDefault(); selectTab(tabId); }}>
                    {tabLabels[tabId]}
                  </a>
                </li>
              ))}
            </ul>

            {hasOverflowTabs && (
              <div className="cc-tabs-more" style={{ display: hasOverflowTabs ? undefined : 'none' }}>
                <button type="button" className="cc-tabs-more-btn" onClick={toggleMoreMenu}>
                  More <i className="fa fa-chevron-down"></i>
                </button>
                {showMoreMenu && (
                  <ul className="cc-tabs-more-menu" role="menu">
                    {overflowTabIds.map(tabId => (
                      <li key={tabId}>
                        <a href={`#CallCardTab${tabId}`} onClick={(e) => { e.preventDefault(); selectTab(tabId as TabId); setShowMoreMenu(false); }}>
                          {tabLabels[tabId as TabId]}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="cc-action-buttons">
            <div>
              {!isMDT && !callCardNotClosed && hasUpdateClosedCallCardPermissions && !isEditingClosedCallCard && (
                <input type="button" value="Edit Call" className="kstc-btn kstc-btn--sm kstc-btn--danger" onClick={editClosedCC} />
              )}
              {!isMDT && !callCardNotClosed && hasUpdateClosedCallCardPermissions && isEditingClosedCallCard && (
                <input type="button" value="Reclose Call" className="kstc-btn kstc-btn--sm kstc-btn--danger" disabled={!saveOrUpdateButtonEnabled} onClick={recloseCC} />
              )}
              {!isMDT && !callCardNotClosed && hasUpdateClosedCallCardPermissions && isEditingClosedCallCard && (
                <input type="button" value="Cancel" className="kstc-btn kstc-btn--sm kstc-btn--danger" disabled={!saveOrUpdateButtonEnabled} onClick={cancelRecloseCC} />
              )}
              {!isMDT && !callCardNotClosed && hasReopenClosedCallCardPermissions && !isEditingClosedCallCard && (
                <input type="button" value="Reopen Call" className="kstc-btn kstc-btn--sm kstc-btn--danger" onClick={reopenClosedCC} />
              )}
              {hasCCDownloadPermission && !callCardNotClosed && (
                <input type="button" value="Download PDF" className="kstc-btn kstc-btn--sm kstc-btn--success" onClick={downloadCCPdf} />
              )}
            </div>

            {!isMDT && (
              <div>
                {CC.id != null && (
                  <input type="button" value="Create Report" className="kstc-btn kstc-btn--sm kstc-btn--warning" onClick={PrimaryOfficerClearRepReqWithPrompt} />
                )}
                {CC.id == null && (
                  <input type="button" value="Delete Call" className="kstc-btn kstc-btn--sm kstc-btn--danger" onClick={() => setShowDeleteCallModal(true)} />
                )}
                {CC.id != null && callCardNotClosed && hasCloseCallCardPermissions && (
                  <input type="button" value="Close Call" className="kstc-btn kstc-btn--sm kstc-btn--danger" onClick={closeCC} />
                )}
                {isNewCard && (
                  <input type="button" value="Send Call" className="kstc-btn kstc-btn--sm kstc-btn--success" disabled={!saveOrUpdateButtonEnabled} onClick={sendCADCC} />
                )}
                {CC.id != null && callCardNotClosed && (
                  <input type="button" value="Update Call" className="kstc-btn kstc-btn--sm kstc-btn--success" onClick={updateCC} />
                )}
              </div>
            )}
          </div>
        </nav>

        {/* Panel Heading */}
        <div className="panel call-card-info-panel">
          <div className="panel-heading border-blue">
            <div className="call-card-title-area">
              <h5>
                Call for Service <span className="call-card-event-id">{CC.eventId}</span>
                {!callCardNotClosed && <label className="cc-status-badge cc-status-badge--danger">CLOSED CALL</label>}
                {callCardNotClosed && <label className="cc-clock" id="clock">{clockTime}</label>}
              </h5>
            </div>
          </div>
        </div>

        {/* Status Banner */}
        <CallCardStatusBanner
          isMdt={isMDT}
          isEditMode={isEditMode}
          callCardNotClosed={callCardNotClosed}
          isEditingClosedCallCard={isEditingClosedCallCard}
          hasEditClosedCallPermission={hasUpdateClosedCallCardPermissions}
          hasReopenClosedCallPermission={hasReopenClosedCallCardPermissions}
          canEditForm={!isFormDisabled}
          isSelfOnCall={isSelfOnCall()}
          canSelfAssign={canSelfAssignToCall}
          onEnableEditMode={editMDTCC}
          onSelfAssign={addSelfToCallCard}
          onEditClosedCall={editClosedCC}
          onReopenCall={reopenClosedCC}
        />

        {/* Tab Content: Call Details */}
        {activeTab === 'CallDetails' && (
          <CallCardDetailsTab
            CC={CC}
            CCCntr={CCCntr}
            moduleType={moduleType}
            isFormDisabled={isFormDisabled}
            callCardNotClosed={callCardNotClosed}
            isEditingClosedCallCard={isEditingClosedCallCard}
            canConfirmOnSceneLocation={isMDT && isOfficerOnCallCard}
            canEditCallLocation={canEditCallLocation}
            callAlert={callAlert}
            natureOfCallSopLink={natureOfCallSopLink}
            clientLink={clientLink}
            callLocationMapLink={callLocationMapLink}
            patrolZoneNames={patrolZoneNames}
            isShowingClientAddressButton={isShowingClientAddressButton}
            natureOfCallOptions={natureOfCallOptions}
            natureOfCallGroupBy={natureOfCallGroupBy}
            clientOptions={clientOptions}
            siteOptions={siteOptions}
            sitesLoading={sitesLoading}
            dispositionOptions={dispositionOptions}
            dispositionGroupBy={dispositionGroupBy}
            officers={officers}
            availablePeople={availablePeople}
            officersRef={officersRef}
            canSendCallComments={canSendCallComments}
            hasCallCommentUpdatePermission={hasCallCommentUpdatePermission}
            onNatureChange={onNatureOfCallChange}
            onClientChange={onClientChange}
            onSiteChange={onSiteChange}
            onFieldChange={handleDetailsFieldChange}
            onLocationBlur={showDialogIfLocationIsClientLocation}
            onClientAddressClick={updateClientAddressFromServer}
            onDisabledFieldClick={showDisabledFieldFeedback}
            onCommentAddedToNew={handleCommentAddedToNew}
            onCommentUpdated={handleCommentUpdated}
            onOfficersChange={handleOfficersChange}
            onDispositionChange={onDispositionChange}
            onDispositionFieldChange={handleDispositionFieldChange}
            realtimeVersion={realtimeVersion}
            priorityAutoChangeVersion={priorityAutoChangeVersion}
            onNatureOpen={onNatureOfCallOpen}
            onNatureClose={onNatureOfCallClose}
          />
        )}

        {/* Tab Content: People/Vehicles */}
        {activeTab === 'PeopleVehicles' && (
          <CallCardPeopleVehiclesTab
            people={people}
            vehicles={vehicles}
            isReadOnly={isFormDisabled}
            onPeopleChange={handlePeopleChange}
            onVehiclesChange={handleVehiclesChange}
            onLinkPerson={handleLinkPerson}
            onShowLinkedPerson={handleShowLinkedPerson}
            onCreateNpsdPerson={handleCreateNpsdPerson}
            onLinkVehicle={handleLinkVehicle}
            onShowLinkedVehicle={handleShowLinkedVehicle}
            onCreateNpsdVehicle={handleCreateNpsdVehicle}
          />
        )}

        {/* Tab Content: Attachments */}
        {activeTab === 'Attachments' && (
          <CallCardAttachmentsTab
            attachments={attachments}
            isReadOnly={isFormDisabled}
            onUpload={handleAttachmentUpload}
            onRemove={removeAttachment}
          />
        )}

        {/* Tab Content: Citations */}
        {activeTab === 'Citations' && hasCitationViewPermission && (
          <CallCardCitationsTab
            callCardId={CC.id}
            isReadOnly={isFormDisabled}
          />
        )}

        {/* Tab Content: Audit Log */}
        {activeTab === 'AuditLog' && CC.id != null && hasCCAuditLogPermission && (
          <CallCardAuditLogTab
            callCardId={CC.id}
            callCardCreatedBeforeAuditLogging={callCardCreatedBeforeAuditLogging}
          />
        )}

        {/* Tab Content: History */}
        {activeTab === 'History' && CC.id != null && (
          <CallCardHistoryTab
            callLocFormattedAddress={CC.callLocFormattedAddress}
          />
        )}
      </form>

      {/* MDT Bottom Bar */}
      {isMDT && (
        <MdtBottomBar
          callCardNotClosed={callCardNotClosed}
          isEditMode={isEditMode}
          isSelfOnCall={isSelfOnCall()}
          mdtOfficerTimeButtonsEnabled={mdtOfficerTimeButtonsEnabled}
          enrouteDisabled={enrouteDisabled}
          onSceneDisabled={onSceneDisabled}
          clearedDisabled={clearedDisabled}
          classEnrouteBtn={classEnrouteBtn}
          classOnSceneBtn={classOnSceneBtn}
          classClearedBtn={classClearedBtn}
          canEdit={canEnterMDTEditMode}
          hasCloseCallCardPermissions={hasCloseCallCardPermissions}
          hasDisposition={hasDisposition}
          onSelfAssign={addSelfToCallCard}
          onEnRoute={EnRoute}
          onOnScene={OnScene}
          onCleared={Cleared}
          onCreateReport={PrimaryOfficerClearRepReq}
          onEdit={editMDTCC}
          onCancel={cancelMDTCC}
          onUpdate={updateCC}
          onClose={closeCC}
          onShowCloseModal={openCloseCallModal}
          onNavigation={turnByTurnNavigationV2}
          onNavigationSettings={navigationSettings}
        />
      )}

      {/* Disabled Field Toast */}
      <DisabledFieldToast />

      {/* ── Modals ──────────────────────────────────────────────────── */}

      {/* Time Update Modal */}
      <TimeUpdateModal
        open={timeUpdateModal.open}
        title={timeUpdateModal.title}
        message={timeUpdateModal.message}
        initialValue={timeUpdateModal.initialValue}
        onConfirm={handleTimeUpdateConfirm}
        onCancel={handleTimeUpdateCancel}
      />

      {/* Close Call Modal */}
      <CloseCallModal
        open={showCloseCallModal}
        dispositionOptions={dispositionOptions}
        dispositionGroupBy={dispositionGroupBy}
        currentDisposition={CC.callDisposition}
        currentDispositionNotes={CC.dispositionNotes}
        onClose={handleCloseCallConfirm}
        onCancel={() => setShowCloseCallModal(false)}
      />

      {/* SOP Modal */}
      <ViewSopModal
        open={showSopModal}
        title="Standard Operating Procedures"
        sopContent={sopModalContent}
        theme={theme}
        onClose={closeSopModal}
      />

      {/* Client Page Modal */}
      <PageModal
        open={showClientModal}
        title="Client"
        modalId="clientPageModal"
        onClose={closeClientModal}
      >
        {showClientModal && clientModalClientId && (
          <NPSDClientScreenWrapper
            clientId={clientModalClientId}
            isCaseReportMode={true}
            onClientUpdated={handleClientUpdatedInModal}
          />
        )}
      </PageModal>

      {/* Map Page Modal */}
      <PageModal
        open={showMapModal}
        title="Map"
        modalId="mapPageModal"
        onClose={closeMapModal}
      >
        {showMapModal && mapModalAddress && (
          <MapScreen address={mapModalAddress} />
        )}
      </PageModal>

      {/* Person Page Modal */}
      <PageModal
        open={showPersonModal}
        title="Person"
        modalId="personPageModal"
        onClose={closePersonModal}
      >
        {showPersonModal && personModalId && (
          <NPSDPersonScreen
            personId={personModalId}
            isWindow={true}
            onClose={closePersonModal}
            onPersonUpdated={handlePersonUpdatedInModal}
            onPersonSaved={handlePersonUpdatedInModal}
          />
        )}
      </PageModal>

      {/* Vehicle Page Modal */}
      <PageModal
        open={showVehicleModal}
        title="Vehicle"
        modalId="vehiclePageModal"
        onClose={closeVehicleModal}
      >
        {showVehicleModal && vehicleModalId && (
          <NPSDVehicleScreen
            vehicleId={vehicleModalId}
            isWindow={true}
            onClose={closeVehicleModal}
            onUpdated={handleVehicleUpdatedInModal}
            onSaved={handleVehicleUpdatedInModal}
          />
        )}
      </PageModal>

      {/* Connections Search Modal */}
      <SearchModal
        open={connectionsSearch.open}
        searchType={connectionsSearch.type.toLowerCase() as any}
        title={`Link ${connectionsSearch.type} to Call Card`}
        theme={theme}
        initialValues={connectionsSearch.initialValues}
        onSelect={handleConnectionsSearchSelect}
        onView={handleConnectionsSearchView}
        onCancel={closeConnectionsSearchModal}
      />

      {/* Navigation Settings Modal */}
      <ConfirmModal
        open={showNavigationSettingsModal}
        title="Navigation Settings"
        confirmText={savingNavigationSettings ? 'Saving...' : 'Save'}
        confirmDisabled={savingNavigationSettings}
        confirmClass="primary"
        onConfirm={saveNavigationSettings}
        onCancel={closeNavigationSettingsModal}
      >
        <div className="navigation-settings-form">
          <p className="kstc-nav-hint">Select your preferred navigation app:</p>
          <label className="radio-option">
            <input type="radio" name="navigationType"
              value={OrnaNavigationType.GOOGLE_MAPS}
              checked={selectedNavigationType === OrnaNavigationType.GOOGLE_MAPS}
              onChange={() => setSelectedNavigationType(OrnaNavigationType.GOOGLE_MAPS)} />
            Google Maps
          </label>
          <label className="radio-option">
            <input type="radio" name="navigationType"
              value={OrnaNavigationType.WAZE}
              checked={selectedNavigationType === OrnaNavigationType.WAZE}
              onChange={() => setSelectedNavigationType(OrnaNavigationType.WAZE)} />
            Waze
          </label>
          {isInAppNavigationAvailable() && (
            <label className="radio-option">
              <input type="radio" name="navigationType"
                value={OrnaNavigationType.IN_APP}
                checked={selectedNavigationType === OrnaNavigationType.IN_APP}
                onChange={() => setSelectedNavigationType(OrnaNavigationType.IN_APP)} />
              In-App
            </label>
          )}
        </div>
      </ConfirmModal>

      {/* Custom Location Modal */}
      <AddEditCustomLocationModal
        open={showAddCustomLocationModal}
        mode="add"
        customLocation={{
          location: addCustomLocationData.location,
          latitude: addCustomLocationData.coordinates ? parseFloat(addCustomLocationData.coordinates.split(',')[0]) : 0,
          longitude: addCustomLocationData.coordinates ? parseFloat(addCustomLocationData.coordinates.split(',')[1]) : 0,
        }}
        onSave={handleSaveCustomLocation}
        onCancel={cancelAddCustomLocationDialog}
      />

      {/* Delete Call Confirm Modal */}
      <ConfirmModal
        open={showDeleteCallModal}
        title="Delete Call"
        messageHtml="Are you sure you want to delete this call? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteCall}
        onCancel={() => setShowDeleteCallModal(false)}
      />

      {/* Create Case Report Confirm Modal */}
      <ConfirmModal
        open={showCreateCaseReportModal}
        title="Create Case Report"
        messageHtml="One Case Report can be made per Call."
        confirmText="Create"
        cancelText="Cancel"
        confirmClass="success"
        onConfirm={confirmCreateCaseReport}
        onCancel={cancelCreateCaseReport}
      />

      {/* Client Match Modal */}
      <ConfirmModal
        open={clientMatchModal.open}
        title="Client Match"
        message={`Call location matches client \u201C${clientMatchModal.clientName}\u201D. Select the client?`}
        confirmText="Yes"
        cancelText="No"
        confirmClass="success"
        onConfirm={confirmClientMatch}
        onCancel={closeClientMatchModal}
      />

    </div>
  );
}

// ============================================================================
// Window Registration
// ============================================================================

(window as any).CallCardScreenReact = CallCardScreen;

// ============================================================================
// Screen Registry Wrapper — handles data loading that previously happened
// in CAD_CallCard / MDT_ShowCallCard init functions + CallCard constructor
// ============================================================================


interface CallCardScreenWrapperProps {
  callCardId?: number;
  moduleType: string; // 'cad' | 'mdt'
  mapLat?: number;
  mapLng?: number;
  customLocationName?: string;
}

export function CallCardScreenWrapper({ callCardId, moduleType: moduleTypeStr, mapLat, mapLng, customLocationName }: CallCardScreenWrapperProps) {
  const [loaded, setLoaded] = useState(false);
  const [ccState, setCCState] = useState<any>(null);
  const [ccCntrState, setCCCntrState] = useState<any>(null);
  const [modType, setModType] = useState<any>(null);

  useEffect(() => {
    const moduleType = moduleTypeStr === 'cad' ? ModuleType.cad : ModuleType.mdt;
    setModType(moduleType);

    (window as any).resetCallCardClockRefresh();
    (window as any).newCallCard = false;

    const id = callCardId;
    const hasId = hasNumericValue(id) && id! > 0;

    if (hasId) {
      (window as any).subscribeToCallCardUpdates(id, moduleType);
    }

    api.getAvailableCADOfficers(id, (availableOfficersResult: any) => {
      processCallCardAvailableOfficers(availableOfficersResult);

      const onDataReady = (result: any) => {
        const cc = new (window as any).CallCard(result, moduleType, false);
        if (cc.callDateTime === null) {
          cc.callDateTime = new Date();
        }
        App.currentCC = cc;
        App.currentCCOriginal = new (window as any).CallCard(result, moduleType, false);
        if (App.currentCCOriginal.callDateTime === null) {
          App.currentCCOriginal.callDateTime = cc.callDateTime;
        }

        const cntr = new (window as any).CallCardController();
        cntr.cachedOfficerList = availableOfficersResult?.officerList || [];
        cntr.callComments = [];
        cntr.moduleType = moduleType;
        App.currentCCCntr = cntr;

        // Fetch comments
        if (hasValue(cc.id)) {
          api.listCallComments(cc.id, (commentResult: any) => {
            if (!App.currentCCCntr) return;
            const comments: any[] = [];
            for (const entry of commentResult.entries) {
              comments.push(new CallComment(entry));
            }
            App.currentCCCntr.callComments = comments.concat(App.currentCCCntr.callComments);
            sortAndRemoveDuplicates(App.currentCCCntr.callComments, cntr.callCommentCompare);
          }, () => {});
        }

        setCallCardScreenLoaded(true);
        setCallCardRealtimeEnabled(true);
        setCCState(cc);
        setCCCntrState(cntr);
        setLoaded(true);

        if (hasValue((window as any)._currentCallCardLoadedCallback)) {
          (window as any)._currentCallCardLoadedCallback();
          (window as any)._currentCallCardLoadedCallback = null;
        }
      };

      if (hasId) {
        if (moduleTypeStr === 'cad') {
          api.getCallCardById(id, true, onDataReady);
        } else {
          api.getMDTCallCard(id!.toString(), true, onDataReady);
        }
      } else {
        (window as any).newCallCard = true;
        onDataReady(undefined);

        // Set location from map context menu if provided
        if (customLocationName) {
          App.currentCC.callLocFormattedAddress = customLocationName;
          App.currentCCOriginal.callLocFormattedAddress = customLocationName;
        } else if (hasNumericValue(mapLat) && hasNumericValue(mapLng)) {
          App.currentCC.callLocFormattedAddress = mapLat + ',' + mapLng;
          App.currentCCOriginal.callLocFormattedAddress = mapLat + ',' + mapLng;
        }
      }
    });
  }, [callCardId, moduleTypeStr, mapLat, mapLng, customLocationName]);

  if (!loaded || !ccState || !ccCntrState) return (
    <div style={{ padding: '48px', textAlign: 'center' }}>
      <div className="kst-loading"></div>
    </div>
  );
  return <CallCardScreen CC={ccState} CCCntr={ccCntrState} moduleType={modType} />;
}
